<!-- LessonCreationForm.vue -->
<template>
	<div class="lesson-creation-form">
		<div class="lesson-creation-form__container">
			<div v-if="isOffline" class="form-offline-banner" role="status">
				Нет интернета. Сохранение будет поставлено в очередь ({{ pendingQueueCount }}).
			</div>
			<img
				class="lesson-creation-form__background"
				alt=""
				src="/assets/animaapp/rectangle.svg"
			/>

			<!-- Настройки урока -->
			<div class="lesson-settings">
				<div class="lesson-settings__header">
					<div class="lesson-settings__header-background" />
					<div class="lesson-settings__header-title">
						{{ isEditMode ? 'Редактирование урока' : 'Создание урока' }}
					</div>
					<button
						class="lesson-settings__close-button"
						type="button"
						@click="handleClose"
						aria-label="Закрыть"
					>
						<img
							class="practice-settings__close-icon1"
							alt="Закрыть"
							src="@/assets/cdn-animaapp/vector-8.svg"
						/>
					</button>
				</div>

				<!-- Название урока -->
				<div class="lesson-settings__field">
					<label class="lesson-settings__label">
						Название урока
						<span class="lesson-required-mark" aria-hidden="true">*</span>
					</label>
					<input
						v-model="lessonTitle"
						type="text"
						class="lesson-settings__input"
						:class="{ 'lesson-settings__input--error': lessonTitleError }"
						placeholder="Введите название урока"
					/>
					<p v-if="lessonTitleError" class="lesson-content__error">
						{{ lessonTitleError }}
					</p>
				</div>

				<!-- Индикатор автосохранения -->
				<div v-if="lastSaveTimeFormatted" class="lesson-settings__autosave">
					<span class="lesson-settings__autosave-text"
						>Последнее сохранение: {{ lastSaveTimeFormatted }}</span
					>
				</div>

				<!-- Тип урока -->
				<div class="lesson-settings__type-section">
					<h3 class="lesson-settings__section-title">Тип урока</h3>

					<div class="lesson-settings__type-buttons">
						<button
							type="button"
							class="lesson-type-btn"
							:class="{ 'lesson-type-btn--active': lessonType === 'video' }"
							@click="lessonType = 'video'"
						>
							<div class="lesson-type-btn__heading">
								<div class="lesson-type-btn__icon">
									<svg
										width="35"
										height="22"
										viewBox="0 0 30 17"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M2.48926 0.5H17.5107C18.6887 0.500147 19.5 1.29679 19.5 2.11621V14.8838C19.5 15.7032 18.6887 16.4999 17.5107 16.5H2.48926C1.3113 16.4999 0.5 15.7032 0.5 14.8838V2.11621C0.5 1.29679 1.3113 0.500147 2.48926 0.5ZM27.627 2.09961C28.0304 1.86495 28.5175 1.86597 28.9033 2.03418C29.2916 2.20357 29.5 2.50014 29.5 2.81152V14.1846C29.4999 14.4978 29.291 14.794 28.9023 14.9629C28.5155 15.131 28.0286 15.1299 27.6279 14.8955L22.167 11.6973V5.30176L27.627 2.09961Z"
											stroke="#0095EC"
										/>
									</svg>
								</div>
								<div class="lesson-type-btn__title">Видео урок</div>
							</div>

							<div class="lesson-type-btn__description">
								Урок на основе видео с дополнительными материалами
							</div>
						</button>

						<button
							type="button"
							class="lesson-type-btn"
							:class="{ 'lesson-type-btn--active': lessonType === 'article' }"
							@click="lessonType = 'article'"
						>
							<div class="lesson-type-btn__heading">
								<div class="lesson-type-btn__icon">
									<svg
										width="25"
										height="20"
										viewBox="0 0 25 20"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<mask
											id="path-1-outside-1_9928_4139"
											maskUnits="userSpaceOnUse"
											x="0"
											y="-0.787109"
											width="25"
											height="21"
											fill="black"
										>
											<rect fill="white" y="-0.787109" width="25" height="21" />
											<path
												d="M22.1719 1.00195C22.875 0.962891 23.5 1.50977 23.4609 2.17383V15.377C23.4609 16.002 22.9531 16.5488 22.2891 16.5879C20.375 16.666 16.4688 17.0566 13.7734 18.4238C13.3438 18.6191 12.8359 18.3457 12.8359 17.877V3.6582C12.8359 3.46289 12.9531 3.26758 13.1484 3.15039C15.7656 1.54883 20.0234 1.11914 22.1719 1.00195ZM11.3125 3.15039C11.5078 3.26758 11.625 3.46289 11.625 3.6582V17.877C11.625 18.3457 11.1172 18.6191 10.6875 18.4238C7.99219 17.0566 4.08594 16.666 2.17188 16.5879C1.50781 16.5488 1 16.002 1 15.377V2.17383C1 1.50977 1.58594 0.962891 2.28906 1.00195C4.4375 1.11914 8.69531 1.54883 11.3125 3.15039Z"
											/>
										</mask>
										<path
											d="M22.1719 1.00195L22.2263 2.00047L22.2273 2.00041L22.1719 1.00195ZM23.4609 2.17383L22.4627 2.11511L22.4609 2.14444V2.17383H23.4609ZM22.2891 16.5879L22.3298 17.5871L22.3388 17.5867L22.3478 17.5862L22.2891 16.5879ZM13.7734 18.4238L14.1872 19.3342L14.2067 19.3253L14.2258 19.3157L13.7734 18.4238ZM13.1484 3.15039L13.663 4.00792L13.6704 4.00336L13.1484 3.15039ZM11.3125 3.15039L10.7905 4.00339L10.798 4.00788L11.3125 3.15039ZM10.6875 18.4238L10.2351 19.3157L10.2542 19.3253L10.2737 19.3342L10.6875 18.4238ZM2.17188 16.5879L2.11315 17.5862L2.12212 17.5867L2.13109 17.5871L2.17188 16.5879ZM2.28906 1.00195L2.23359 2.00041L2.2346 2.00047L2.28906 1.00195ZM22.1719 1.00195L22.2273 2.00041C22.3054 1.99608 22.379 2.02553 22.4266 2.06827C22.4487 2.08812 22.4581 2.10457 22.4612 2.11158C22.4627 2.11487 22.4629 2.11619 22.4628 2.11573C22.4627 2.11511 22.4627 2.11461 22.4627 2.11511L23.4609 2.17383L24.4592 2.23255C24.5397 0.864185 23.3027 -0.0624123 22.1164 0.00349271L22.1719 1.00195ZM23.4609 2.17383H22.4609V15.377H23.4609H24.4609V2.17383H23.4609ZM23.4609 15.377H22.4609C22.4609 15.4603 22.3781 15.5809 22.2303 15.5896L22.2891 16.5879L22.3478 17.5862C23.5282 17.5167 24.4609 16.5436 24.4609 15.377H23.4609ZM22.2891 16.5879L22.2483 15.5887C20.3157 15.6676 16.2219 16.0606 13.3211 17.532L13.7734 18.4238L14.2258 19.3157C16.7156 18.0527 20.4343 17.6644 22.3298 17.5871L22.2891 16.5879ZM13.7734 18.4238L13.3596 17.5135C13.4445 17.4749 13.5472 17.4787 13.6372 17.5297C13.7377 17.5868 13.8359 17.7163 13.8359 17.877H12.8359H11.8359C11.8359 19.208 13.2229 19.7725 14.1872 19.3342L13.7734 18.4238ZM12.8359 17.877H13.8359V3.6582H12.8359H11.8359V17.877H12.8359ZM12.8359 3.6582H13.8359C13.8359 3.86375 13.7237 3.97142 13.6629 4.00788L13.1484 3.15039L12.6339 2.2929C12.1825 2.56374 11.8359 3.06203 11.8359 3.6582H12.8359ZM13.1484 3.15039L13.6704 4.00336C14.8288 3.29448 16.4109 2.81482 18.0265 2.50121C19.6259 2.19073 21.1783 2.05764 22.2263 2.00047L22.1719 1.00195L22.1174 0.0034374C21.0171 0.063457 19.3663 0.203798 17.6454 0.537856C15.9406 0.868774 14.0852 1.40474 12.6265 2.29742L13.1484 3.15039ZM11.3125 3.15039L10.798 4.00788C10.7372 3.97142 10.625 3.86375 10.625 3.6582H11.625H12.625C12.625 3.06203 12.2784 2.56374 11.827 2.2929L11.3125 3.15039ZM11.625 3.6582H10.625V17.877H11.625H12.625V3.6582H11.625ZM11.625 17.877H10.625C10.625 17.7163 10.7232 17.5868 10.8238 17.5297C10.9138 17.4787 11.0165 17.4749 11.1013 17.5135L10.6875 18.4238L10.2737 19.3342C11.2381 19.7725 12.625 19.208 12.625 17.877H11.625ZM10.6875 18.4238L11.1399 17.532C8.23902 16.0606 4.14521 15.6676 2.21266 15.5887L2.17188 16.5879L2.13109 17.5871C4.02667 17.6644 7.74535 18.0527 10.2351 19.3157L10.6875 18.4238ZM2.17188 16.5879L2.2306 15.5896C2.08288 15.5809 2 15.4603 2 15.377H1H0C0 16.5436 0.932745 17.5167 2.11315 17.5862L2.17188 16.5879ZM1 15.377H2V2.17383H1H0V15.377H1ZM1 2.17383H2C2 2.14323 2.01273 2.10157 2.05658 2.06137C2.09971 2.02184 2.16078 1.99637 2.23359 2.00041L2.28906 1.00195L2.34453 0.00349271C1.09885 -0.0657119 0 0.905444 0 2.17383H1ZM2.28906 1.00195L2.2346 2.00047C3.28268 2.05764 4.83503 2.19073 6.43444 2.50121C8.05002 2.81482 9.63212 3.29448 10.7905 4.00336L11.3125 3.15039L11.8345 2.29742C10.3757 1.40474 8.52029 0.868774 6.81556 0.537856C5.09466 0.203798 3.44389 0.063457 2.34353 0.0034374L2.28906 1.00195Z"
											fill="black"
											mask="url(#path-1-outside-1_9928_4139)"
										/>
									</svg>
								</div>
								<div class="lesson-type-btn__title">Статья</div>
							</div>

							<div class="lesson-type-btn__description">
								Текстовый урок с возможностью форматирования
							</div>
						</button>
					</div>
				</div>
			</div>

			<!-- Настройки содержания урока -->
			<div class="lesson-content">
				<h3 class="lesson-content__title">Содержание урока</h3>

				<!-- Для видео урока -->
				<div v-if="lessonType === 'video'" class="lesson-content__video">
					<div class="lesson-section lesson-section--video">
						<h4 class="lesson-section__title">Загрузите видео</h4>
						<div class="lesson-section__card">
							<div class="lesson-content__field lesson-content__field--inline">
								<label class="lesson-content__label">
									Файл видео
									<span class="lesson-required-mark" aria-hidden="true">*</span>
								</label>
								<div class="lesson-file-upload">
									<input
										ref="videoFileInput"
										type="file"
										accept="video/*"
										@change="handleVideoUpload"
										class="lesson-content__file-input"
										id="videoFileInput"
									/>
									<label for="videoFileInput" class="lesson-file-upload__label">
										<span class="lesson-file-upload__icon">
											<svg
												width="20"
												height="20"
												viewBox="0 0 20 20"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
											>
												<path
													d="M10.5684 0.250174C10.4227 0.0907898 10.2167 0 10.0007 0C9.78471 0 9.57876 0.0907898 9.43301 0.250174L5.33043 4.73735C5.04376 5.05089 5.06555 5.53746 5.37909 5.82412C5.69263 6.11079 6.17919 6.089 6.46586 5.77547L9.23148 2.75056V14.1026C9.23148 14.5274 9.57589 14.8718 10.0007 14.8718C10.4255 14.8718 10.7699 14.5274 10.7699 14.1026V2.75056L13.5356 5.77547C13.8222 6.089 14.3088 6.11079 14.6223 5.82412C14.9359 5.53746 14.9576 5.05089 14.671 4.73735L10.5684 0.250174Z"
													fill="white"
												/>
												<path
													d="M1.53846 13.0768C1.53846 12.652 1.19407 12.3076 0.769231 12.3076C0.344401 12.3076 5.37388e-07 12.652 5.37388e-07 13.0768V13.1332C-1.99754e-05 14.5358 -4.05617e-05 15.6664 0.119508 16.5556C0.243631 17.4788 0.50916 18.256 1.12651 18.8734C1.74387 19.4908 2.52117 19.7563 3.44436 19.8804C4.33356 19.9999 5.46414 19.9999 6.8668 19.9999H13.1332C14.5359 19.9999 15.6665 19.9999 16.5557 19.8804C17.4789 19.7563 18.2561 19.4908 18.8735 18.8734C19.4909 18.256 19.7564 17.4788 19.8805 16.5556C20 15.6664 20 14.5358 20 13.1332V13.0768C20 12.652 19.6556 12.3076 19.2308 12.3076C18.806 12.3076 18.4615 12.652 18.4615 13.0768C18.4615 14.5491 18.4599 15.5758 18.3558 16.3506C18.2546 17.1032 18.0695 17.5017 17.7856 17.7856C17.5017 18.0695 17.1033 18.2545 16.3507 18.3557C15.5759 18.4598 14.5491 18.4615 13.0769 18.4615H6.92308C5.45086 18.4615 4.42407 18.4598 3.64935 18.3557C2.89677 18.2545 2.49823 18.0695 2.21437 17.7856C1.93051 17.5017 1.74544 17.1032 1.64426 16.3506C1.54009 15.5758 1.53846 14.5491 1.53846 13.0768Z"
													fill="white"
												/>
											</svg>
										</span>
										<span class="lesson-file-upload__text"
											>Загрузить видео</span
										>
									</label>
								</div>
								<p class="lesson-content__hint">
									Максимальный размер: {{ VIDEO_MAX_SIZE_MB }} МБ
								</p>
								<p v-if="videoFileError" class="lesson-content__error">
									{{ videoFileError }}
								</p>

								<div v-if="videoFile" class="lesson-file-item">
									<div class="lesson-file-item__info">
										<span class="lesson-file-item__name">
											{{ getVideoFileDisplayName() }}
										</span>
										<span
											v-if="videoFile && videoFile.size && isNewFile(videoFile)"
											class="lesson-file-item__size"
										>
											{{ formatFileSize(videoFile.size) }}
										</span>
									</div>
									<button
										type="button"
										class="lesson-file-item__remove"
										@click="removeVideo"
										title="Удалить видео"
									>
										✕
									</button>
								</div>
							</div>

							<div class="lesson-content__cover-upload">
								<label class="lesson-content__label">Обложка видео</label>
								<div class="lesson-file-upload">
									<input
										ref="videoCoverFileInput"
										type="file"
										accept="image/*"
										@change="handleVideoCoverUpload"
										class="lesson-content__file-input"
										id="videoCoverFileInput"
									/>
									<label for="videoCoverFileInput" class="lesson-file-upload__label">
										<span class="lesson-file-upload__icon">
											<svg
												width="20"
												height="20"
												viewBox="0 0 20 20"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
											>
												<path
													d="M10.5684 0.250174C10.4227 0.0907898 10.2167 0 10.0007 0C9.78471 0 9.57876 0.0907898 9.43301 0.250174L5.33043 4.73735C5.04376 5.05089 5.06555 5.53746 5.37909 5.82412C5.69263 6.11079 6.17919 6.089 6.46586 5.77547L9.23148 2.75056V14.1026C9.23148 14.5274 9.57589 14.8718 10.0007 14.8718C10.4255 14.8718 10.7699 14.5274 10.7699 14.1026V2.75056L13.5356 5.77547C13.8222 6.089 14.3088 6.11079 14.6223 5.82412C14.9359 5.53746 14.9576 5.05089 14.671 4.73735L10.5684 0.250174Z"
													fill="white"
												/>
												<path
													d="M1.53846 13.0768C1.53846 12.652 1.19407 12.3076 0.769231 12.3076C0.344401 12.3076 5.37388e-07 12.652 5.37388e-07 13.0768V13.1332C-1.99754e-05 14.5358 -4.05617e-05 15.6664 0.119508 16.5556C0.243631 17.4788 0.50916 18.256 1.12651 18.8734C1.74387 19.4908 2.52117 19.7563 3.44436 19.8804C4.33356 19.9999 5.46414 19.9999 6.8668 19.9999H13.1332C14.5359 19.9999 15.6665 19.9999 16.5557 19.8804C17.4789 19.7563 18.2561 19.4908 18.8735 18.8734C19.4909 18.256 19.7564 17.4788 19.8805 16.5556C20 15.6664 20 14.5358 20 13.1332V13.0768C20 12.652 19.6556 12.3076 19.2308 12.3076C18.806 12.3076 18.4615 12.652 18.4615 13.0768C18.4615 14.5491 18.4599 15.5758 18.3558 16.3506C18.2546 17.1032 18.0695 17.5017 17.7856 17.7856C17.5017 18.0695 17.1033 18.2545 16.3507 18.3557C15.5759 18.4598 14.5491 18.4615 13.0769 18.4615H6.92308C5.45086 18.4615 4.42407 18.4598 3.64935 18.3557C2.89677 18.2545 2.49823 18.0695 2.21437 17.7856C1.93051 17.5017 1.74544 17.1032 1.64426 16.3506C1.54009 15.5758 1.53846 14.5491 1.53846 13.0768Z"
													fill="white"
												/>
											</svg>
										</span>
										<span class="lesson-file-upload__text">Загрузить обложку</span>
									</label>
								</div>
								<p class="lesson-content__hint">
									Максимальный размер: {{ VIDEO_COVER_MAX_SIZE_MB }} МБ
								</p>
								<p v-if="videoCoverError" class="lesson-content__error">
									{{ videoCoverError }}
								</p>
								<div
									v-if="videoCoverPreviewUrl"
									class="lesson-content__cover-preview"
								>
									<img
										:src="videoCoverPreviewUrl"
										alt="Обложка видео"
										class="lesson-content__cover-image"
									/>
								</div>
								<div v-if="videoCoverFile" class="lesson-file-item">
									<div class="lesson-file-item__info">
										<span class="lesson-file-item__name">
											{{ getVideoCoverDisplayName() }}
										</span>
										<span
											v-if="
												videoCoverFile &&
												videoCoverFile.size &&
												isNewFile(videoCoverFile)
											"
											class="lesson-file-item__size"
										>
											{{ formatFileSize(videoCoverFile.size) }}
										</span>
									</div>
									<button
										type="button"
										class="lesson-file-item__remove"
										@click="removeVideoCover"
										title="Удалить обложку"
									>
                    ✕
									</button>
								</div>
							</div>

							<div class="lesson-content__field lesson-content__field--url">
								<label class="lesson-content__label" style="margin-top: 7px">
									Или вставьте ссылку на YouTube/Vimeo
									<span class="lesson-required-mark" aria-hidden="true">*</span>
								</label>
								<div class="lesson-content__url-row">
									<input
										v-model="videoLink"
										type="text"
										class="lesson-content__input"
										placeholder="https://www.youtube.com/..."
									/>
									<button
										type="button"
										class="lesson-content__check-button"
										@click="handleVideoLinkChange"
									>
										Проверить
									</button>
								</div>
								<p v-if="videoLinkError" class="lesson-content__error">
									{{ videoLinkError }}
								</p>
							</div>
						</div>
					</div>

					<div
						v-if="videoPreviewUrl || youtubeEmbedUrl"
						class="lesson-content__preview"
					>
						<video
							v-if="videoPreviewUrl && !youtubeEmbedUrl"
							id="lesson-player"
							class="video-js lesson-video"
							:src="videoPreviewUrl"
							controls
							preload="auto"
						></video>
						<iframe
							v-else-if="youtubeEmbedUrl"
							class="lesson-video"
							:src="youtubeEmbedUrl"
							frameborder="0"
							allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
							allowfullscreen
						></iframe>
					</div>

					<div class="lesson-section lesson-section--description">
						<h4 class="lesson-section__title">Описание урока</h4>
						<div class="lesson-section__card">
							<TopicTextEditor
								v-model="lessonDescription"
								editor-class="lesson-content__textarea"
								placeholder="Введите описание урока..."
								:min-height="140"
							/>
						</div>
					</div>

					<div class="lesson-section lesson-section--materials">
						<h4 class="lesson-section__title">Дополнительные материалы</h4>
						<div class="lesson-section__card">
							<div class="lesson-content__field">
								<div class="lesson-materials-upload">
									<input
										ref="materialsFileInput"
										type="file"
										:accept="MATERIAL_ACCEPT"
										multiple
										@change="handleMaterialsUpload"
										class="lesson-content__file-input"
										id="materialsFileInput"
									/>
									<label
										for="materialsFileInput"
										class="lesson-materials-upload__label"
									>
										<span class="lesson-materials-upload__icon">
											<svg
												width="20"
												height="20"
												viewBox="0 0 20 20"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
											>
												<path
													d="M10.5684 0.250174C10.4227 0.0907898 10.2167 0 10.0007 0C9.78471 0 9.57876 0.0907898 9.43301 0.250174L5.33043 4.73735C5.04376 5.05089 5.06555 5.53746 5.37909 5.82412C5.69263 6.11079 6.17919 6.089 6.46586 5.77547L9.23148 2.75056V14.1026C9.23148 14.5274 9.57589 14.8718 10.0007 14.8718C10.4255 14.8718 10.7699 14.5274 10.7699 14.1026V2.75056L13.5356 5.77547C13.8222 6.089 14.3088 6.11079 14.6223 5.82412C14.9359 5.53746 14.9576 5.05089 14.671 4.73735L10.5684 0.250174Z"
													fill="white"
												/>
												<path
													d="M1.53846 13.0768C1.53846 12.652 1.19407 12.3076 0.769231 12.3076C0.344401 12.3076 5.37388e-07 12.652 5.37388e-07 13.0768V13.1332C-1.99754e-05 14.5358 -4.05617e-05 15.6664 0.119508 16.5556C0.243631 17.4788 0.50916 18.256 1.12651 18.8734C1.74387 19.4908 2.52117 19.7563 3.44436 19.8804C4.33356 19.9999 5.46414 19.9999 6.8668 19.9999H13.1332C14.5359 19.9999 15.6665 19.9999 16.5557 19.8804C17.4789 19.7563 18.2561 19.4908 18.8735 18.8734C19.4909 18.256 19.7564 17.4788 19.8805 16.5556C20 15.6664 20 14.5358 20 13.1332V13.0768C20 12.652 19.6556 12.3076 19.2308 12.3076C18.806 12.3076 18.4615 12.652 18.4615 13.0768C18.4615 14.5491 18.4599 15.5758 18.3558 16.3506C18.2546 17.1032 18.0695 17.5017 17.7856 17.7856C17.5017 18.0695 17.1033 18.2545 16.3507 18.3557C15.5759 18.4598 14.5491 18.4615 13.0769 18.4615H6.92308C5.45086 18.4615 4.42407 18.4598 3.64935 18.3557C2.89677 18.2545 2.49823 18.0695 2.21437 17.7856C1.93051 17.5017 1.74544 17.1032 1.64426 16.3506C1.54009 15.5758 1.53846 14.5491 1.53846 13.0768Z"
													fill="white"
												/>
											</svg>
										</span>
										<span class="lesson-materials-upload__text"
											>Загрузить файлы</span
										>
									</label>
								</div>
								<div class="lesson-materials-upload__hint">
									<span class="lesson-materials-upload__description">
										Добавьте инструкции, шаблоны или примеры
									</span>
									<br />
									<span class="lesson-materials-upload__size-hint">
										Максимальный размер каждого файла: 50 МБ
									</span>
								</div>
								<p v-if="materialsError" class="lesson-content__error">
									{{ materialsError }}
								</p>
							</div>
							<div v-if="materials.length" class="lesson-materials-list">
								<div
									v-for="(file, index) in materials"
									:key="index"
									class="lesson-material-item"
								>
									<div class="lesson-material-item__info">
										<span class="lesson-material-item__name">
											{{ normalizeFileName(file.name || file.file_name) }}
										</span>
										<span class="lesson-material-item__size">
											{{ formatFileSize(file.size || file.file_size || 0) }}
										</span>
										<span
											v-if="isNewFile(file)"
											class="lesson-material-item__badge"
										>
											Новый
										</span>
									</div>
									<button
										type="button"
										class="lesson-material-item__remove"
										@click="removeMaterial(index)"
										title="Удалить файл"
									>
										✕
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Для статьи -->
				<div v-if="lessonType === 'article'" class="lesson-content__article">
					<!-- Блок загрузки документа -->
					<div class="lesson-section lesson-section--doc-upload">
						<h4 class="lesson-section__title">Загрузка документа</h4>
						<div class="lesson-section__card lesson-section__card--upload">
							<div class="lesson-doc-upload">
								<input
									ref="docFileInput"
									type="file"
									:accept="DOC_ACCEPT"
									@change="handleDocUpload"
									class="lesson-content__file-input"
									id="docFileInput"
								/>
								<label for="docFileInput" class="lesson-doc-upload__label">
									<span class="lesson-doc-upload__icon">
										<svg
											width="20"
											height="20"
											viewBox="0 0 20 20"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M10.5684 0.250174C10.4227 0.0907898 10.2167 0 10.0007 0C9.78471 0 9.57876 0.0907898 9.43301 0.250174L5.33043 4.73735C5.04376 5.05089 5.06555 5.53746 5.37909 5.82412C5.69263 6.11079 6.17919 6.089 6.46586 5.77547L9.23148 2.75056V14.1026C9.23148 14.5274 9.57589 14.8718 10.0007 14.8718C10.4255 14.8718 10.7699 14.5274 10.7699 14.1026V2.75056L13.5356 5.77547C13.8222 6.089 14.3088 6.11079 14.6223 5.82412C14.9359 5.53746 14.9576 5.05089 14.671 4.73735L10.5684 0.250174Z"
												fill="white"
											/>
											<path
												d="M1.53846 13.0768C1.53846 12.652 1.19407 12.3076 0.769231 12.3076C0.344401 12.3076 5.37388e-07 12.652 5.37388e-07 13.0768V13.1332C-1.99754e-05 14.5358 -4.05617e-05 15.6664 0.119508 16.5556C0.243631 17.4788 0.50916 18.256 1.12651 18.8734C1.74387 19.4908 2.52117 19.7563 3.44436 19.8804C4.33356 19.9999 5.46414 19.9999 6.8668 19.9999H13.1332C14.5359 19.9999 15.6665 19.9999 16.5557 19.8804C17.4789 19.7563 18.2561 19.4908 18.8735 18.8734C19.4909 18.256 19.7564 17.4788 19.8805 16.5556C20 15.6664 20 14.5358 20 13.1332V13.0768C20 12.652 19.6556 12.3076 19.2308 12.3076C18.806 12.3076 18.4615 12.652 18.4615 13.0768C18.4615 14.5491 18.4599 15.5758 18.3558 16.3506C18.2546 17.1032 18.0695 17.5017 17.7856 17.7856C17.5017 18.0695 17.1033 18.2545 16.3507 18.3557C15.5759 18.4598 14.5491 18.4615 13.0769 18.4615H6.92308C5.45086 18.4615 4.42407 18.4598 3.64935 18.3557C2.89677 18.2545 2.49823 18.0695 2.21437 17.7856C1.93051 17.5017 1.74544 17.1032 1.64426 16.3506C1.54009 15.5758 1.53846 14.5491 1.53846 13.0768Z"
												fill="white"
											/>
										</svg>
									</span>
									<span class="lesson-doc-upload__text">Загрузить файл</span>
								</label>
								<div class="lesson-doc-upload__hint">
									<span class="lesson-doc-upload__description">
										Загрузите документ в формате PDF или DOCX
									</span>
								</div>
								<p v-if="docFileError" class="lesson-content__error">
									{{ docFileError }}
								</p>
							</div>

							<!-- Показать загруженный файл -->
							<div v-if="docFile" class="lesson-materials-list">
								<div class="lesson-material-item">
									<div class="lesson-material-item__info">
										<span class="lesson-material-item__name">
											{{ docFile.name || docFile.file_name }}
										</span>
										<span class="lesson-material-item__size">
											{{
												formatFileSize(docFile.size || docFile.file_size || 0)
											}}
										</span>
										<span
											v-if="isNewFile(docFile)"
											class="lesson-material-item__badge"
										>
											Новый
										</span>
									</div>
									<button
										type="button"
										class="lesson-material-item__remove"
										@click="removeDocFile"
										title="Удалить файл"
									>
										✕
									</button>
								</div>
							</div>
						</div>
					</div>

					<!-- Блок создания статьи -->
					<div class="lesson-section lesson-section--article-toggle">
						<button
							type="button"
							class="lesson-content__toggle-article-button"
							:class="{
								'lesson-content__toggle-article-button--disabled':
									!canEditArticleContent,
							}"
							:disabled="!canEditArticleContent"
							:title="articleEditorBlockedHint"
							v-text="articleToggleButtonText"
							@click="toggleArticleEditor"
						>
						</button>
						<p
							v-if="!canEditArticleContent"
							class="lesson-content__toggle-article-hint"
							v-text="articleEditorLockedMessage"
						>
						</p>
					</div>

					<div
						v-show="canEditArticleContent && showArticleEditor"
						class="lesson-section lesson-section--article-editor"
					>
						<RichTextEditor
							v-model="articleContent"
							:lesson-id="editingLessonId"
							:initial-height="600"
							label="Создание статьи"
							placeholder="Начните писать статью…"
						/>
					</div>

					<!-- 🔒 Ограничения скачивания -->
					<div class="lesson-section lesson-section--download-settings">
						<h4 class="lesson-section__title">Ограничения скачивания</h4>
						<div class="lesson-section__card">
							<div class="lesson-settings-row">
								<label class="lesson-checkbox">
									<input
										type="checkbox"
										v-model="disableMainMaterialsDownload"
										class="lesson-checkbox__input"
									/>
									<span class="lesson-checkbox__mark"></span>
									<span class="lesson-checkbox__label"
										>Запретить скачивание документа статьи</span
									>
								</label>
							</div>
							<div class="lesson-settings-row">
								<label class="lesson-checkbox">
									<input
										type="checkbox"
										v-model="disableAdditionalMaterialsDownload"
										class="lesson-checkbox__input"
									/>
									<span class="lesson-checkbox__mark"></span>
									<span class="lesson-checkbox__label"
										>Запретить скачивание дополнительных материалов</span
									>
								</label>
							</div>
						</div>
					</div>

					<div class="lesson-section lesson-section--materials">
						<h4 class="lesson-section__title">Дополнительные материалы</h4>
						<div class="lesson-section__card">
							<div class="lesson-content__field">
								<div class="lesson-materials-upload">
									<input
										ref="materialsFileInputArticle"
										type="file"
										:accept="MATERIAL_ACCEPT"
										multiple
										@change="handleMaterialsUpload"
										class="lesson-content__file-input"
										id="materialsFileInputArticle"
									/>
									<label
										for="materialsFileInputArticle"
										class="lesson-materials-upload__label"
									>
										<span class="lesson-materials-upload__icon">
											<svg
												width="20"
												height="20"
												viewBox="0 0 20 20"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
											>
												<path
													d="M10.5684 0.250174C10.4227 0.0907898 10.2167 0 10.0007 0C9.78471 0 9.57876 0.0907898 9.43301 0.250174L5.33043 4.73735C5.04376 5.05089 5.06555 5.53746 5.37909 5.82412C5.69263 6.11079 6.17919 6.089 6.46586 5.77547L9.23148 2.75056V14.1026C9.23148 14.5274 9.57589 14.8718 10.0007 14.8718C10.4255 14.8718 10.7699 14.5274 10.7699 14.1026V2.75056L13.5356 5.77547C13.8222 6.089 14.3088 6.11079 14.6223 5.82412C14.9359 5.53746 14.9576 5.05089 14.671 4.73735L10.5684 0.250174Z"
													fill="white"
												/>
												<path
													d="M1.53846 13.0768C1.53846 12.652 1.19407 12.3076 0.769231 12.3076C0.344401 12.3076 5.37388e-07 12.652 5.37388e-07 13.0768V13.1332C-1.99754e-05 14.5358 -4.05617e-05 15.6664 0.119508 16.5556C0.243631 17.4788 0.50916 18.256 1.12651 18.8734C1.74387 19.4908 2.52117 19.7563 3.44436 19.8804C4.33356 19.9999 5.46414 19.9999 6.8668 19.9999H13.1332C14.5359 19.9999 15.6665 19.9999 16.5557 19.8804C17.4789 19.7563 18.2561 19.4908 18.8735 18.8734C19.4909 18.256 19.7564 17.4788 19.8805 16.5556C20 15.6664 20 14.5358 20 13.1332V13.0768C20 12.652 19.6556 12.3076 19.2308 12.3076C18.806 12.3076 18.4615 12.652 18.4615 13.0768C18.4615 14.5491 18.4599 15.5758 18.3558 16.3506C18.2546 17.1032 18.0695 17.5017 17.7856 17.7856C17.5017 18.0695 17.1033 18.2545 16.3507 18.3557C15.5759 18.4598 14.5491 18.4615 13.0769 18.4615H6.92308C5.45086 18.4615 4.42407 18.4598 3.64935 18.3557C2.89677 18.2545 2.49823 18.0695 2.21437 17.7856C1.93051 17.5017 1.74544 17.1032 1.64426 16.3506C1.54009 15.5758 1.53846 14.5491 1.53846 13.0768Z"
													fill="white"
												/>
											</svg>
										</span>
										<span class="lesson-materials-upload__text"
											>Загрузить файлы</span
										>
									</label>
								</div>
								<div class="lesson-materials-upload__hint">
									<span class="lesson-materials-upload__description">
										Добавьте инструкции, шаблоны или примеры
									</span>
									<br />
									<span class="lesson-materials-upload__size-hint">
										Максимальный размер каждого файла: 50 МБ
									</span>
								</div>
								<p v-if="materialsError" class="lesson-content__error">
									{{ materialsError }}
								</p>
							</div>
							<div v-if="materials.length" class="lesson-materials-list">
								<div
									v-for="(file, index) in materials"
									:key="index"
									class="lesson-material-item"
								>
									<div class="lesson-material-item__info">
										<span class="lesson-material-item__name">
											{{ normalizeFileName(file.name || file.file_name) }}
										</span>
										<span class="lesson-material-item__size">
											{{ formatFileSize(file.size || file.file_size || 0) }}
										</span>
										<span
											v-if="isNewFile(file)"
											class="lesson-material-item__badge"
										>
											Новый
										</span>
									</div>
									<button
										type="button"
										class="lesson-material-item__remove"
										@click="removeMaterial(index)"
										title="Удалить файл"
									>
										✕
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Кнопки действий -->
				<div class="lesson-actions">
					<span
						class="lesson-actions__preview-tooltip"
						:title="lessonPreviewDisabledHint"
					>
						<button
							type="button"
							class="lesson-actions__button lesson-actions__button--ghost"
							:disabled="!canOpenLessonPreview"
							@click="openLessonPreviewInNewTab"
						>
							Предпросмотр урока
						</button>
					</span>
					<div class="lesson-actions__primary-group">
						<button
							type="button"
							class="lesson-actions__button lesson-actions__button--primary"
							:class="{
								'lesson-actions__button--disabled':
									isSaveButtonDisabled || isSaving,
							}"
							:disabled="isSaveButtonDisabled || isSaving"
							@click="handleSaveLesson"
						>
							{{ saveButtonText }}
						</button>
						<p v-if="getLessonSubmitErrorMessage()" class="lesson-actions__error">
							{{ getLessonSubmitErrorMessage() }}
						</p>
					</div>
					<button
						v-if="false"
						type="button"
						class="lesson-actions__button lesson-actions__button--secondary"
						@click="handleSaveDraft"
					>
						Сохранить как черновик
					</button>
				</div>

				<!-- Модальное окно предпросмотра урока -->
				<teleport to="body">
					<transition name="modal-fade">
						<div
							v-if="showPreviewModal"
							class="lesson-preview-overlay"
							@click.self="handleClosePreview"
						>
							<div class="lesson-preview">
								<!-- Шапка -->
								<div class="lesson-preview__header">
									<div class="lesson-preview__header-content">
										<span class="lesson-preview__badge">
											{{
												lessonType === 'video' ? '🎬 Видео-урок' : '📄 Статья'
											}}
										</span>
										<h2 class="lesson-preview__title">
											{{ lessonTitle || 'Без названия' }}
										</h2>
									</div>
									<button
										type="button"
										class="lesson-preview__close"
										@click="handleClosePreview"
										title="Закрыть"
									>
										<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
											<path
												d="M18 6L6 18M6 6l12 12"
												stroke="currentColor"
												stroke-width="2"
												stroke-linecap="round"
											/>
										</svg>
									</button>
								</div>

								<!-- Контент -->
								<div class="lesson-preview__body">
									<!-- Видео секция -->
									<div
										v-if="lessonType === 'video'"
										class="lesson-preview__video-section"
									>
										<div
											v-if="youtubeEmbedUrl"
											class="lesson-preview__video-wrapper"
										>
											<iframe
												:src="youtubeEmbedUrl"
												class="lesson-preview__video-iframe"
												frameborder="0"
												allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
												allowfullscreen
											></iframe>
										</div>
										<div
											v-else-if="videoPreviewUrl"
											class="lesson-preview__video-wrapper"
										>
											<video
												:src="videoPreviewUrl"
												class="lesson-preview__video-player"
												controls
											></video>
										</div>
										<div v-else class="lesson-preview__video-placeholder">
											<div class="lesson-preview__video-placeholder-icon">
												🎬
											</div>
											<p class="lesson-preview__video-placeholder-text">
												Видео не загружено
											</p>
										</div>
									</div>

									<!-- Описание урока -->
									<div
										v-if="hasMeaningfulText(lessonDescription)"
										class="lesson-preview__description"
									>
										<h3 class="lesson-preview__section-title">
											Описание урока
										</h3>
										<div
											class="lesson-preview__description-text"
											v-html="sanitizePreviewHtml(lessonDescription)"
										></div>
									</div>

									<!-- Загруженный документ (для статьи) -->
									<div
										v-if="lessonType === 'article' && docFile"
										class="lesson-preview__document"
									>
										<h3 class="lesson-preview__section-title">
											📄 Загруженный документ
										</h3>
										<div class="lesson-preview__document-item">
											<div class="lesson-preview__document-icon">
												{{ getFileIcon(docFile.name || docFile.file_name) }}
											</div>
											<div class="lesson-preview__document-info">
												<span class="lesson-preview__document-name">
													{{ docFile.name || docFile.file_name }}
												</span>
												<span class="lesson-preview__document-size">
													{{
														formatFileSize(
															docFile.size || docFile.file_size || 0,
														)
													}}
												</span>
											</div>
										</div>
									</div>

									<!-- Контент статьи -->
									<div
										v-if="lessonType === 'article' && articleContent"
										class="lesson-preview__article"
									>
										<h3 class="lesson-preview__section-title">
											Содержание статьи
										</h3>
										<CollapsibleArticle
											:content="articleContent"
											class="lesson-preview__article-content"
										/>
									</div>

									<!-- Дополнительные материалы -->
									<div
										v-if="materials.length > 0"
										class="lesson-preview__materials"
									>
										<h3 class="lesson-preview__section-title">
											📎 Дополнительные материалы
											<span class="lesson-preview__materials-count">{{
												materials.length
											}}</span>
										</h3>
										<div class="lesson-preview__materials-list">
											<div
												v-for="(file, index) in materials"
												:key="index"
												class="lesson-preview__material-item"
											>
												<div class="lesson-preview__material-icon">
													{{ getFileIcon(file.name || file.file_name) }}
												</div>
												<div class="lesson-preview__material-info">
										<span class="lesson-preview__material-name">
											{{ normalizeFileName(file.name || file.file_name) }}
										</span>
													<span class="lesson-preview__material-size">
														{{
															formatFileSize(file.size || file.file_size || 0)
														}}
													</span>
												</div>
												<button
													type="button"
													class="lesson-preview__material-download"
													title="Скачать"
												>
													⬇️
												</button>
											</div>
										</div>
									</div>

									<!-- Информация о публикации -->
									<div class="lesson-preview__meta">
										<div class="lesson-preview__meta-item">
											<span class="lesson-preview__meta-label"
												>Публикация:</span
											>
											<span class="lesson-preview__meta-value">
												{{
													isScheduledPublish
														? `По расписанию: ${publishDate} ${publishTime}`
														: 'Сразу после сохранения'
												}}
											</span>
										</div>
									</div>
								</div>

								<!-- Футер -->
								<div class="lesson-preview__footer">
									<span class="lesson-preview__footer-hint">
										Так урок будет выглядеть для студентов
									</span>
									<button
										type="button"
										class="lesson-preview__footer-btn"
										@click="handleClosePreview"
									>
										Закрыть предпросмотр
									</button>
								</div>
							</div>
						</div>
					</transition>
				</teleport>
			</div>
		</div>
	</div>
</template>

<script>
import AutoPublishSettings from './test-creation/AutoPublishSettings.vue'
import DOMPurify from 'dompurify'
import RichTextEditor from '@/components/ProjectCreateCourse/common/RichTextEditor.vue'
import TopicTextEditor from '../../../../components/ProjectCreateCourse/common/TopicTextEditor.vue'
import CollapsibleArticle from '@/components/common/CollapsibleArticle.vue'
import { openInternalRouteInContext } from '@/utils/bitrixInternalNavigation'

const VIDEO_MAX_SIZE_MB = 25
const VIDEO_COVER_MAX_SIZE_MB = 10
const MATERIAL_MAX_SIZE_MB = 50
const VIDEO_MAX_SIZE_BYTES = VIDEO_MAX_SIZE_MB * 1024 * 1024
const VIDEO_COVER_MAX_SIZE_BYTES = VIDEO_COVER_MAX_SIZE_MB * 1024 * 1024
const MATERIAL_MAX_SIZE_BYTES = MATERIAL_MAX_SIZE_MB * 1024 * 1024

// Ограничения загрузки файлов (материалы урока)
const MATERIAL_ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'pdf', 'docx']
const MATERIAL_ALLOWED_MIME_TYPES = [
	'image/jpeg',
	'image/png',
	'image/gif',
	'application/pdf',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]
const MATERIAL_ACCEPT = '.jpg,.jpeg,.png,.gif,.pdf,.docx'
const DOC_ACCEPT = '.pdf,.docx'

export default {
	name: 'LessonCreationForm',
	components: {
		AutoPublishSettings,
		RichTextEditor,
		TopicTextEditor,
		CollapsibleArticle,
	},
	props: {
		currentModuleId: {
			type: [String, Number],
			default: null,
		},
		availableModules: {
			type: Array,
			default: () => [],
		},
		editingLesson: {
			type: Object,
			default: null,
		},
	},
	emits: ['save-lesson', 'save-draft', 'cancel'],
	data() {
		return {
			editingLessonId: null,
			lessonTitle: '',
			lessonType: 'video',
			lessonDescription: '',
			isScheduledPublish: false,
			publishDate: '',
			publishTime: '',
			videoFile: null,
			videoPreviewUrl: '',
			videoCoverFile: null,
			videoCoverPreviewUrl: '',
			videoCoverError: '',
			videoLink: '',
			videoLinkError: '',
			videoFileError: '',
			lessonTitleError: '',
			youtubeEmbedUrl: '',
			materials: [],
			materialsError: '',
			deletedMaterials: [],
			articleContent: '',
			showArticleEditor: false,
			docFile: null,
			docFileError: '',
			deletedDocFile: false,
			showPreviewModal: false,
			lastSaveTime: null,
			VIDEO_MAX_SIZE_MB,
			VIDEO_COVER_MAX_SIZE_MB,
			MATERIAL_MAX_SIZE_MB,
			MATERIAL_ACCEPT,
			DOC_ACCEPT,
			isSaved: false,
			isLoadingData: false, //  Флаг загрузки данных
			originalData: null, //  Снимок исходного состояния

			// 📚 Настройки статьи (TOC)
			enableToc: false, // Включить оглавление
			tocMode: 'auto', // 'auto' (по H2/H3) или 'manual'

			// 🔒 Ограничения скачивания
			disableMainMaterialsDownload: false, // Запретить скачивание основных материалов
			disableAdditionalMaterialsDownload: false, // Запретить скачивание доп. материалов
			isOffline: typeof navigator !== 'undefined' ? !navigator.onLine : false,
			pendingQueueCount: 0,
			submitQueueKey: 'lesson_form_submit_queue_v1',
			isSaving: false,
		}
	},
	computed: {
		isEditMode() {
			return this.editingLessonId !== null
		},
		lastSaveTimeFormatted() {
			if (!this.lastSaveTime) return null
			const date = new Date(this.lastSaveTime)
			return date.toLocaleTimeString('ru-RU', {
				hour: '2-digit',
				minute: '2-digit',
			})
		},

		//  ПЕРЕНЕСИТЕ hasUnsavedChanges СЮДА (удалите из methods!)
		hasUnsavedChanges() {
			// Если форма была успешно сохранена — изменений нет
			// Если нет снимка исходных данных — значит это создание нового урока
			if (!this.originalData) {
				return !!(
					this.lessonTitle ||
					this.lessonDescription ||
					this.articleContent ||
					this.videoFile ||
					this.videoCoverFile ||
					this.videoCoverPreviewUrl ||
					this.videoLink ||
					this.docFile ||
					this.materials.length > 0 ||
					this.isScheduledPublish ||
					this.publishDate ||
					this.publishTime ||
					this.enableToc ||
					this.tocMode !== 'auto' ||
					this.disableMainMaterialsDownload ||
					this.disableAdditionalMaterialsDownload
				)
			}

			// Сравниваем текущее состояние с исходным
			if (this.lessonTitle !== this.originalData.lessonTitle) return true
			if (this.lessonType !== this.originalData.lessonType) return true
			if (this.lessonDescription !== this.originalData.lessonDescription)
				return true
			if (this.articleContent !== this.originalData.articleContent) return true
			if (this.videoLink !== this.originalData.videoLink) return true
			if (this.isScheduledPublish !== this.originalData.isScheduledPublish)
				return true
			if (this.publishDate !== this.originalData.publishDate) return true
			if (this.publishTime !== this.originalData.publishTime) return true
			if (this.deletedDocFile !== this.originalData.deletedDocFile) return true

			// Сравнение файлов
			const currentVideoFileName = this.videoFile
				? this.videoFile.name || this.videoFile.file_name || ''
				: ''
			if (currentVideoFileName !== this.originalData.videoFileName) return true
			const currentVideoCoverFileName = this.videoCoverFile
				? this.videoCoverFile.name || this.videoCoverFile.file_name || ''
				: ''
			if (currentVideoCoverFileName !== this.originalData.videoCoverFileName)
				return true

			const currentDocFileName = this.docFile
				? this.docFile.name || this.docFile.file_name || ''
				: ''
			if (currentDocFileName !== this.originalData.docFileName) return true

			// Сравнение материалов
			const currentMaterialNames = this.materials.map(
				m => m.name || m.file_name || '',
			)
			if (
				JSON.stringify(currentMaterialNames) !==
				JSON.stringify(this.originalData.materialFileNames)
			) {
				return true
			}

			// Сравнение настроек статьи
			const currentEnableToc = !!this.enableToc
			const originalEnableToc = !!(this.originalData
				? this.originalData.enableToc
				: false)
			if (currentEnableToc !== originalEnableToc) {
				console.log(
					'Change detected: enableToc',
					currentEnableToc,
					originalEnableToc,
				)
				return true
			}

			if (
				this.tocMode !==
				(this.originalData ? this.originalData.tocMode : 'auto')
			) {
				console.log(
					'Change detected: tocMode',
					this.tocMode,
					this.originalData.tocMode,
				)
				return true
			}

			// Сравнение ограничений скачивания
			const currentDisableMain = !!this.disableMainMaterialsDownload
			const originalDisableMain = !!(this.originalData
				? this.originalData.disableMainMaterialsDownload
				: false)

			if (currentDisableMain !== originalDisableMain) {
				console.log(
					'Change detected: disableMain',
					currentDisableMain,
					originalDisableMain,
				)
				return true
			}

			const currentDisableAdd = !!this.disableAdditionalMaterialsDownload
			const originalDisableAdd = !!(this.originalData
				? this.originalData.disableAdditionalMaterialsDownload
				: false)

			if (currentDisableAdd !== originalDisableAdd) {
				console.log(
					'Change detected: disableAdd',
					currentDisableAdd,
					originalDisableAdd,
				)
				return true
			}

			return false
		},

		//  Теперь БЕЗ скобок — это computed свойство!
		saveButtonText() {
			if (this.isEditMode) {
				return this.hasUnsavedChanges ? 'Применить изменения' : 'Сохранено'
			}
			return 'Создать урок'
		},

		//  БЕЗ скобок!
		isSaveButtonDisabled() {
			const hasTitle = !!(this.lessonTitle && this.lessonTitle.trim())
			if (this.isEditMode) {
				return !this.hasUnsavedChanges || !hasTitle
			}
			return !hasTitle
		},

		//  Динамический текст кнопки
		saveButtonText() {
			if (this.isEditMode) {
				// В режиме редактирования
				const hasChanges = this.hasUnsavedChanges
				return hasChanges ? 'Применить изменения' : 'Сохранено'
			} else {
				// В режиме создания
				return 'Создать урок'
			}
		},
		//  Отключаем кнопку если нет изменений в режиме редактирования
		isSaveButtonDisabled() {
			const hasTitle = !!(this.lessonTitle && this.lessonTitle.trim())
			if (this.isEditMode) {
				return !this.hasUnsavedChanges || !hasTitle
			}
			return !hasTitle // В режиме создания блокируем сохранение без названия
		},
		canOpenLessonPreview() {
			return (
				this.currentModuleId !== null &&
				this.currentModuleId !== undefined &&
				this.currentModuleId !== '' &&
				this.editingLessonId !== null &&
				this.editingLessonId !== undefined &&
				this.editingLessonId !== '' &&
				!String(this.currentModuleId).startsWith('temp_') &&
				!String(this.editingLessonId).startsWith('temp_')
			)
		},
		lessonPreviewDisabledHint() {
			return this.canOpenLessonPreview
				? ''
				: 'Сначала создайте урок, чтобы открыть предпросмотр.'
		},
		canEditArticleContent() {
			return this.isEditMode
		},
		articleEditorBlockedHint() {
			return this.canEditArticleContent
				? ''
				: '\u0421\u043d\u0430\u0447\u0430\u043b\u0430 \u0441\u043e\u0437\u0434\u0430\u0439\u0442\u0435 \u0443\u0440\u043e\u043a, \u0430 \u043f\u043e\u0442\u043e\u043c \u043e\u0442\u043a\u0440\u043e\u0439\u0442\u0435 \u0431\u043b\u043e\u043a \u0441\u0442\u0430\u0442\u044c\u0438.'
		},
		articleEditorLockedMessage() {
			return '\u0420\u0435\u0434\u0430\u043a\u0442\u043e\u0440 \u0441\u0442\u0430\u0442\u044c\u0438 \u0441\u0442\u0430\u043d\u0435\u0442 \u0434\u043e\u0441\u0442\u0443\u043f\u0435\u043d \u043f\u043e\u0441\u043b\u0435 \u043f\u0435\u0440\u0432\u043e\u0433\u043e \u0441\u043e\u0445\u0440\u0430\u043d\u0435\u043d\u0438\u044f \u0443\u0440\u043e\u043a\u0430.'
		},
		articleToggleButtonText() {
			if (!this.canEditArticleContent) {
				return '\u0421\u043d\u0430\u0447\u0430\u043b\u0430 \u0441\u043e\u0437\u0434\u0430\u0439\u0442\u0435 \u0443\u0440\u043e\u043a'
			}
			return this.showArticleEditor
				? '\u0421\u043a\u0440\u044b\u0442\u044c \u0431\u043b\u043e\u043a \u0441\u0442\u0430\u0442\u044c\u0438'
				: '\u041d\u0430\u043f\u0438\u0441\u0430\u0442\u044c \u0441\u0442\u0430\u0442\u044c\u044e'
		},
	},
	mounted() {
		this.restoreFormData()
		this.pendingQueueCount = this.getSubmitQueue().length
		window.addEventListener('online', this.handleOnline)
		window.addEventListener('offline', this.handleOffline)
		if (!this.isOffline) {
			this.flushSubmitQueue()
		}
	},
	watch: {
		editingLesson: {
			handler(newLesson) {
				if (newLesson) {
					this.loadEditingLesson(newLesson)
				} else {
					this.editingLessonId = null
					this.lessonTitle = ''
					this.lessonType = 'video'
					this.lessonDescription = ''
					this.isScheduledPublish = false
					this.publishDate = ''
					this.publishTime = ''
					this.clearVideo()
					this.removeVideoCover(false)
					this.materials = []
					this.materialsError = ''
					this.deletedMaterials = []
					this.docFile = null
					this.docFileError = ''
					this.articleContent = ''
					this.showArticleEditor = false
					this.showPreviewModal = false
					this.lastSaveTime = null
					this.isSaved = false
					this.originalData = null //  Сбросить снимок
					// Очищаем сохраненные данные формы при сбросе
					this.clearFormData()
				}
			},
			immediate: true,
			deep: false,
		},

		isScheduledPublish: {
			handler() {
				if (!this.isLoadingData) {
					this.saveFormData()
					this.isSaved = false
				}
			},
		},
		publishDate: {
			handler() {
				if (!this.isLoadingData) {
					this.saveFormData()
					this.isSaved = false
				}
			},
		},
		publishTime: {
			handler() {
				if (!this.isLoadingData) {
					this.saveFormData()
					this.isSaved = false
				}
			},
		},
		// Автосохранение данных формы
		lessonTitle: {
			handler() {
				if (this.lessonTitleError && this.lessonTitle && this.lessonTitle.trim()) {
					this.lessonTitleError = ''
				}
				if (!this.isLoadingData) {
					this.saveFormData()
					this.isSaved = false
				}
			},
		},
		lessonType: {
			handler() {
				if (this.lessonType !== 'article') {
					this.showArticleEditor = false
				} else if (this.hasArticleText(this.articleContent)) {
					this.showArticleEditor = true
				}
				if (!this.isLoadingData) {
					this.saveFormData()
					this.isSaved = false
				}
			},
		},
		lessonDescription: {
			handler() {
				if (!this.isLoadingData) {
					this.saveFormData()
					this.isSaved = false
				}
			},
		},
		articleContent: {
			handler() {
				if (!this.isLoadingData) {
					this.saveFormData()
					this.isSaved = false
				}
			},
		},
		videoLink: {
			handler() {
				if (!this.isLoadingData) {
					this.saveFormData()
					this.isSaved = false
				}
			},
		},
		materials: {
			handler() {
				if (!this.isLoadingData) {
					this.isSaved = false
				}
			},
			deep: true,
		},
		videoFile: {
			handler() {
				if (!this.isLoadingData) {
					this.isSaved = false
				}
			},
		},
		videoCoverFile: {
			handler() {
				if (!this.isLoadingData) {
					this.isSaved = false
				}
			},
		},
		docFile: {
			handler() {
				if (!this.isLoadingData) {
					this.isSaved = false
				}
			},
		},
		enableToc: {
			handler() {
				if (!this.isLoadingData) {
					this.saveFormData()
					this.isSaved = false
				}
			},
		},
		tocMode: {
			handler() {
				if (!this.isLoadingData) {
					this.saveFormData()
					this.isSaved = false
				}
			},
		},
		disableMainMaterialsDownload: {
			handler() {
				if (!this.isLoadingData) {
					this.saveFormData()
					this.isSaved = false
				}
			},
		},
		disableAdditionalMaterialsDownload: {
			handler() {
				if (!this.isLoadingData) {
					this.saveFormData()
					this.isSaved = false
				}
			},
		},
	},
	beforeUnmount() {
		this.revokeVideoPreview()
		this.revokeVideoCoverPreview()
		window.removeEventListener('online', this.handleOnline)
		window.removeEventListener('offline', this.handleOffline)
	},
	methods: {
		getLessonSubmitErrorMessage() {
			return (
				this.lessonTitleError ||
				this.videoFileError ||
				this.videoCoverError ||
				this.videoLinkError ||
				this.docFileError ||
				this.materialsError ||
				''
			)
		},
		getVideoFileDisplayName() {
			const name =
				this.videoFile?.name || this.videoFile?.file_name || this.videoFile || ''
			return this.normalizeFileName(
				typeof name === 'string' ? this.extractFileNameFromPath(name) : '',
			)
		},
		getVideoCoverDisplayName() {
			const name =
				this.videoCoverFile?.name ||
				this.videoCoverFile?.file_name ||
				this.videoCoverFile ||
				''
			return this.normalizeFileName(
				typeof name === 'string' ? this.extractFileNameFromPath(name) : '',
			)
		},
		extractFileNameFromPath(value) {
			if (!value || typeof value !== 'string') return ''
			const normalized = value.replace(/\\/g, '/')
			const parts = normalized.split('/')
			return parts[parts.length - 1] || value
		},
		handleOnline() {
			this.isOffline = false
			this.flushSubmitQueue()
		},
		handleOffline() {
			this.isOffline = true
		},
		getSubmitQueue() {
			try {
				const raw = localStorage.getItem(this.submitQueueKey)
				const parsed = raw ? JSON.parse(raw) : []
				return Array.isArray(parsed) ? parsed : []
			} catch (error) {
				console.error('Ошибка чтения очереди отправки урока:', error)
				return []
			}
		},
		setSubmitQueue(queue) {
			try {
				localStorage.setItem(this.submitQueueKey, JSON.stringify(queue))
			} catch (error) {
				console.error('Ошибка записи очереди отправки урока:', error)
			}
			this.pendingQueueCount = Array.isArray(queue) ? queue.length : 0
		},
		enqueueSubmit(eventName, payload) {
			const queue = this.getSubmitQueue()
			queue.push({ eventName, payload, createdAt: Date.now() })
			this.setSubmitQueue(queue)
		},
		flushSubmitQueue() {
			if (typeof navigator !== 'undefined' && !navigator.onLine) return
			const queue = this.getSubmitQueue()
			if (!queue.length) return
			queue.forEach(item => {
				if (item && item.eventName && item.payload) {
					this.$emit(item.eventName, item.payload)
				}
			})
			this.setSubmitQueue([])
		},
		validateLessonLogic() {
			this.lessonTitleError = ''
			const normalizedTitle = (this.lessonTitle || '').trim()
			if (!normalizedTitle) {
				this.lessonTitleError = 'Название урока обязательно.'
				return false
			}
			this.lessonTitle = normalizedTitle

			if (this.lessonType === 'video') {
				const hasVideoFile = !!this.videoFile
				const hasVideoLink = !!(this.videoLink && this.videoLink.trim())
				if (!hasVideoFile && !hasVideoLink) {
					this.videoFileError = 'Для видео-урока добавьте файл или ссылку на видео.'
					return false
				}
			}
			return true
		},
		isNewFile(file) {
			return typeof File !== 'undefined' && file instanceof File
		},
		normalizeFileName(name) {
			if (!name) return ''
			if (!/[ÐÑ]/.test(name)) return name
			try {
				const bytes = Uint8Array.from(
					Array.from(name, ch => ch.charCodeAt(0)),
				)
				const decoded = new TextDecoder('utf-8').decode(bytes)
				return decoded && !decoded.includes('�') ? decoded : name
			} catch (error) {
				return name
			}
		},
		formatFileSize(bytes) {
			if (!bytes) return '0 Bytes'
			const k = 1024
			const sizes = ['Bytes', 'KB', 'MB', 'GB']
			const i = Math.floor(Math.log(bytes) / Math.log(k))
			return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`
		},
		getFileIcon(filename) {
			if (!filename) return '📄'
			const ext = filename.split('.').pop()?.toLowerCase()
			const icons = {
				pdf: '📕',
				doc: '📘',
				docx: '📘',
				xls: '📗',
				xlsx: '📗',
				ppt: '📙',
				pptx: '📙',
				zip: '🗜️',
				rar: '🗜️',
				'7z': '🗜️',
				jpg: '🖼️',
				jpeg: '🖼️',
				png: '🖼️',
				gif: '🖼️',
				webp: '🖼️',
				mp4: '🎬',
				avi: '🎬',
				mov: '🎬',
				mkv: '🎬',
				mp3: '🎵',
				wav: '🎵',
				ogg: '🎵',
				txt: '📝',
				md: '📝',
				js: '💻',
				ts: '💻',
				py: '💻',
				java: '💻',
			}
			return icons[ext] || '📄'
		},
		revokeVideoPreview() {
			if (this.videoPreviewUrl && this.videoPreviewUrl.startsWith('blob:')) {
				URL.revokeObjectURL(this.videoPreviewUrl)
			}
			this.videoPreviewUrl = ''
		},
		revokeVideoCoverPreview() {
			if (
				this.videoCoverPreviewUrl &&
				this.videoCoverPreviewUrl.startsWith('blob:')
			) {
				URL.revokeObjectURL(this.videoCoverPreviewUrl)
			}
			this.videoCoverPreviewUrl = ''
		},
		loadEditingLesson(data) {
			if (!data) {
				return
			}
			//  Блокируем watchers на время загрузки
			this.isLoadingData = true

			// Основные поля урока
			this.editingLessonId = data.id || null
			this.lessonTitle = data.title || ''
			this.lessonType = data.type || data.lesson_type || 'video'
			//  description — краткое описание урока (отдельное поле)
			this.lessonDescription = data.description || ''
			//  articleContent — контент статьи (для типа article)
			this.articleContent =
				data.articleContent || data.article_content || data.content || ''
			this.isSaved = false
			// Настройки публикации: поддерживаем camelCase и snake_case
			const publishType = data.publishType || data.publish_type || null
			const scheduledAt = data.scheduled_publish_at || ''

			this.isScheduledPublish =
				publishType === 'scheduled' || Boolean(scheduledAt)

			if (data.publishDate || data.publish_date) {
				this.publishDate = data.publishDate || data.publish_date || ''
				this.publishTime = data.publishTime || data.publish_time || ''
			} else if (scheduledAt) {
				const [datePart, timePart] = scheduledAt.split(' ')
				this.publishDate = datePart || ''
				this.publishTime = timePart || ''
			} else {
				this.publishDate = ''
				this.publishTime = ''
			}

			// Материалы: учитываем разные варианты названий полей
			const rawMaterials =
				(Array.isArray(data.materials) && data.materials) ||
				(Array.isArray(data.additionalMaterials) && data.additionalMaterials) ||
				(Array.isArray(data.additional_materials) &&
					data.additional_materials) ||
				[]
			console.log('📎 Загруженные материалы урока:', rawMaterials)
			this.materials = [...rawMaterials]
			this.deletedMaterials = []
			this.materialsError = ''
			this.videoLinkError = ''
			this.videoFileError = ''
			this.videoCoverError = ''
			this.docFileError = ''
			this.deletedDocFile = false
			this.showPreviewModal = false

			// Документ статьи: загружаем из БД если есть
			const docFilePath = data.docFile || data.doc_file_path || null
			if (docFilePath && typeof docFilePath === 'string') {
				// Создаём объект с информацией о файле из БД
				// Используем оригинальное имя файла если есть
				const docFileName =
					data.doc_file_name || docFilePath.split('/').pop() || 'document'
				const docFileSize = data.doc_file_size || 0
				this.docFile = {
					file_path: docFilePath,
					file_name: docFileName,
					name: docFileName,
					file_size: docFileSize,
					size: docFileSize,
					fromDb: true,
				}
			} else if (this.isNewFile(docFilePath)) {
				this.docFile = docFilePath
			} else {
				this.docFile = null
			}

			// Видео
			this.clearVideo()

			const videoUrl = data.videoUrl || data.video_url || ''

			if (this.isNewFile(data.videoFile)) {
				this.videoFile = data.videoFile
				this.videoLink = ''
				this.youtubeEmbedUrl = ''
				this.revokeVideoPreview()
				this.videoPreviewUrl = URL.createObjectURL(data.videoFile)
			} else if (videoUrl) {
				this.videoLink = videoUrl
				if (this.isYouTubeUrl(videoUrl)) {
					this.youtubeEmbedUrl = this.getYouTubeEmbedUrl(videoUrl)
					this.revokeVideoPreview()
				} else if (this.isVimeoUrl(videoUrl)) {
					this.youtubeEmbedUrl = this.getVimeoEmbedUrl(videoUrl)
					this.revokeVideoPreview()
				} else if (/^https?:\/\//i.test(videoUrl)) {
					this.youtubeEmbedUrl = ''
					this.revokeVideoPreview()
					this.videoPreviewUrl = videoUrl
				} else if (this.isLocalVideoPath(videoUrl)) {
					this.youtubeEmbedUrl = ''
					this.revokeVideoPreview()
					this.videoPreviewUrl = this.normalizeLocalVideoPath(videoUrl)
					this.videoLink = ''
					const videoFileName =
						data.video_file_name ||
						this.extractFileNameFromPath(videoUrl) ||
						'video'
					this.videoFile = {
						file_path: this.normalizeLocalVideoPath(videoUrl),
						file_name: videoFileName,
						name: videoFileName,
						fromDb: true,
					}
				} else {
					this.youtubeEmbedUrl = ''
					this.revokeVideoPreview()
				}
			}
			const coverUrl =
				data.videoCoverUrl ||
				data.video_cover_image_url ||
				data.cover_image_url ||
				data.cover_image ||
				''
			this.removeVideoCover(false)
			if (coverUrl) {
				const normalizedCover = this.normalizeLocalImagePath(coverUrl)
				this.videoCoverPreviewUrl = normalizedCover
				const coverName = this.extractFileNameFromPath(normalizedCover)
				this.videoCoverFile = {
					file_path: normalizedCover,
					file_name: coverName,
					name: coverName,
					fromDb: true,
				}
			}

			// Статья
			if (this.lessonType === 'article') {
				this.articleContent =
					data.articleContent ||
					data.article_content ||
					data.content ||
					this.lessonDescription ||
					''
				this.showArticleEditor = this.hasArticleText(this.articleContent)
			} else {
				this.articleContent = ''
				this.showArticleEditor = false
			}

			// 📚 Настройки статьи
			this.enableToc = data.enableToc || data.enable_toc || false
			this.tocMode = data.tocMode || data.toc_mode || 'auto'

			// 🔒 Ограничения скачивания
			this.disableMainMaterialsDownload =
				data.disableMainMaterialsDownload ||
				data.disable_main_materials_download ||
				false
			this.disableAdditionalMaterialsDownload =
				data.disableAdditionalMaterialsDownload ||
				data.disable_additional_materials_download ||
				false

			//  Сохраняем снимок исходного состояния
			this.captureOriginalData()

			//  Разблокируем watchers
			this.isLoadingData = false
			this.isSaved = true //  Данные загружены, изменений пока нет
		},
		handleVideoUpload(event) {
			const input = event.target
			const file = input.files && input.files[0]
			this.videoFileError = ''
			this.videoLinkError = ''
			if (!file) return
			if ((this.videoFile || this.videoLink) && !window.confirm('Заменить уже добавленное видео?')) {
				if (input) input.value = ''
				return
			}
			if (file.size > VIDEO_MAX_SIZE_BYTES) {
				this.videoFile = null
				this.revokeVideoPreview()
				input.value = ''
				this.videoFileError = `2${file.name}2 слишком большой. Максимальный размер: ${VIDEO_MAX_SIZE_MB} МБ.`
				return
			}
			this.videoFile = file
			this.videoLink = ''
			this.youtubeEmbedUrl = ''
			this.revokeVideoPreview()
			this.videoPreviewUrl = URL.createObjectURL(file)
		},
		handleVideoCoverUpload(event) {
			const input = event.target
			const file = input.files && input.files[0]
			this.videoCoverError = ''
			if (!file) return
			if (!file.type.startsWith('image/')) {
				this.videoCoverError = 'Можно загрузить только изображение.'
				if (input) input.value = ''
				return
			}
			if (file.size > VIDEO_COVER_MAX_SIZE_BYTES) {
				this.videoCoverError = `Файл "${file.name}" слишком большой. Максимальный размер: ${VIDEO_COVER_MAX_SIZE_MB} МБ.`
				if (input) input.value = ''
				return
			}
			this.videoCoverFile = file
			this.revokeVideoCoverPreview()
			this.videoCoverPreviewUrl = URL.createObjectURL(file)
			if (input) input.value = ''
		},
		clearVideo() {
			this.videoFile = null
			this.videoLink = ''
			this.videoLinkError = ''
			this.videoFileError = ''
			this.youtubeEmbedUrl = ''
			this.revokeVideoPreview()
			this.removeVideoCover(false)
			if (this.$refs.videoFileInput) {
				this.$refs.videoFileInput.value = ''
			}
		},
		removeVideoCover(withConfirm = true) {
			if (withConfirm && this.videoCoverFile) {
				const confirmed = window.confirm('Удалить обложку видео?')
				if (!confirmed) return
			}
			this.videoCoverFile = null
			this.videoCoverError = ''
			this.revokeVideoCoverPreview()
			if (this.$refs.videoCoverFileInput) {
				this.$refs.videoCoverFileInput.value = ''
			}
		},
		handleVideoLinkChange() {
			// При вводе ссылки переходим в режим 1видео по ссылке
			if (this.videoFile) {
				this.videoFile = null
				if (this.$refs.videoFileInput) {
					this.$refs.videoFileInput.value = ''
				}
			}

			this.videoFileError = ''
			this.videoLinkError = ''
			this.youtubeEmbedUrl = ''
			this.revokeVideoPreview()

			if (!this.videoLink) {
				this.videoLinkError = 'Введите ссылку на видео.'
				return
			}

			const url = this.videoLink.trim()
			if (this.isYouTubeUrl(url)) {
				this.youtubeEmbedUrl = this.getYouTubeEmbedUrl(url)
			} else if (this.isVimeoUrl(url)) {
				this.youtubeEmbedUrl = this.getVimeoEmbedUrl(url)
			} else if (/^https?:\/\//i.test(url)) {
				this.youtubeEmbedUrl = ''
				this.videoPreviewUrl = url
			} else if (this.isLocalVideoPath(url)) {
				this.youtubeEmbedUrl = ''
				this.videoPreviewUrl = this.normalizeLocalVideoPath(url)
			} else {
				this.videoLinkError = 'Некорректная ссылка на видео.'
			}
		},
		isYouTubeUrl(url) {
			return url.includes('youtube.com') || url.includes('youtu.be')
		},
		getYouTubeEmbedUrl(url) {
			const match =
				url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/) || []
			const videoId = match[1]
			return videoId ? `https://www.youtube-nocookie.com/embed/${videoId}` : url
		},
		isVimeoUrl(url) {
			return url.includes('vimeo.com')
		},
		isLocalVideoPath(url) {
			if (!url || typeof url !== 'string') return false
			const value = url.trim()
			return (
				value.startsWith('/uploads/') ||
				value.startsWith('uploads/') ||
				value.startsWith('/storage/') ||
				value.startsWith('storage/')
			)
		},
		normalizeLocalVideoPath(url) {
			if (!url || typeof url !== 'string') return ''
			const value = url.trim()
			if (!value) return ''
			return value.startsWith('/') ? value : `/${value}`
		},
		normalizeLocalImagePath(url) {
			if (!url || typeof url !== 'string') return ''
			const value = url.trim()
			if (!value) return ''
			if (/^https?:\/\//i.test(value)) return value
			return value.startsWith('/') ? value : `/${value}`
		},
		getVimeoEmbedUrl(url) {
			const match = url.match(/vimeo\.com\/(\d+)/) || []
			const videoId = match[1]
			return videoId ? `https://player.vimeo.com/video/${videoId}` : url
		},

		handleDocUpload(event) {
			const input = event.target
			const file = input.files && input.files[0]
			this.docFileError = ''

			if (!file) {
				this.removeDocFile()
				if (input) input.value = ''
				return
			}
			if (this.docFile && !window.confirm('Заменить уже загруженный файл документа?')) {
				if (input) input.value = ''
				return
			}

			// Проверка типа файла: только PDF / DOCX
			const allowedTypes = [
				'application/pdf',
				'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
			]
			const fileName = file.name || ''
			const ext = fileName.split('.').pop().toLowerCase()
			const isAllowedExt = ['pdf', 'docx'].includes(ext)
			const isAllowedType = allowedTypes.includes(file.type)

			if (!isAllowedType && !isAllowedExt) {
				this.docFileError = `Файл "${file.name}" имеет неподдерживаемый формат. Поддерживаются: PDF, DOCX.`
				if (input) input.value = ''
				return
			}

			// Проверка размера файла
			const size = file.size || file.file_size
			if (size && size > MATERIAL_MAX_SIZE_BYTES) {
				this.docFile = null
				if (input) input.value = ''
				this.docFileError = `Файл "${file.name}" слишком большой. Максимальный размер: ${MATERIAL_MAX_SIZE_MB} МБ.`
				return
			}

			try {
				this.docFile = file
				// Сбрасываем флаг удаления — загружен новый файл
				this.deletedDocFile = false
				if (input) input.value = ''
			} catch (error) {
				this.docFileError = `Ошибка при загрузке файла "${file.name}". Попробуйте еще раз.`
				console.error('Document upload error:', error)
				if (input) input.value = ''
			}
		},

		removeDocFile() {
			if (this.docFile && !window.confirm('Удалить файл документа?')) {
				return
			}
			// Если документ был загружен из БД — помечаем для удаления
			if (this.docFile && !this.isNewFile(this.docFile)) {
				this.deletedDocFile = true
			}
			this.docFile = null
			this.docFileError = ''
		},

		handleMaterialsUpload(event) {
			const input = event.target
			const newFiles = Array.from(input.files || [])
			this.materialsError = ''

			if (!newFiles.length) {
				input.value = ''
				return
			}

			const validFiles = []
			const errors = []
			const maxFiles = 10 // Максимальное количество файлов

			// Проверка общего количества файлов
			if (this.materials.length + newFiles.length > maxFiles) {
				this.materialsError = `Можно загрузить не более ${maxFiles} файлов. Сейчас загружено: ${this.materials.length}.`
				input.value = ''
				return
			}

			for (const file of newFiles) {
				// Проверка типа файла: картинки (JPG/JPEG/PNG/GIF) и документы (PDF/DOCX)
				const fileName = file.name || ''
				const ext = fileName.split('.').pop().toLowerCase()
				const extLabel = ext ? `.${ext}` : ''
				const isAllowedExt = MATERIAL_ALLOWED_EXTENSIONS.includes(ext)
				const isAllowedType = MATERIAL_ALLOWED_MIME_TYPES.includes(file.type)
				if (!isAllowedType && !isAllowedExt) {
					errors.push(
						`Файл "${file.name}" не добавлен: формат "${extLabel}" не поддерживается. Можно загрузить только изображения (JPG, JPEG, PNG, GIF) и документы (PDF, DOCX)`,
					)
					continue
				}

				// Проверка размера файла
				if (file.size && file.size > MATERIAL_MAX_SIZE_BYTES) {
					errors.push(
						`"${file.name}" слишком большой (максимум: ${MATERIAL_MAX_SIZE_MB} МБ)`,
					)
					continue
				}

				// Проверка на дублирование имен файлов
				const isDuplicate = this.materials.some(existingFile => {
					const existingName = existingFile.name || existingFile.file_name
					return existingName === file.name
				})

				if (isDuplicate) {
					errors.push(`"${file.name}" уже загружен`)
					continue
				}

				validFiles.push(file)
			}

			// Показать ошибки, если есть
			if (errors.length > 0) {
				this.materialsError = `Не удалось загрузить некоторые файлы: ${errors.join(
					', ',
				)}.`
			}

			// Добавить валидные файлы
			if (validFiles.length > 0) {
				try {
					this.materials = [...this.materials, ...validFiles]
					if (errors.length === 0) {
						// Показать успешное сообщение только если нет ошибок
						const message =
							validFiles.length === 1
								? `Файл "${validFiles[0].name}" успешно загружен.`
								: `Загружено файлов: ${validFiles.length}.`
						console.log(message)
					}
				} catch (error) {
					this.materialsError =
						'Ошибка при загрузке файлов. Попробуйте еще раз.'
					console.error('Materials upload error:', error)
				}
			}

			input.value = ''
		},
		removeMaterial(index) {
			if (!window.confirm('Удалить материал?')) {
				return
			}
			const material = this.materials[index]
			console.log('🗑️ removeMaterial вызван:', {
				index,
				material,
				isNewFile: this.isNewFile(material),
			})
			if (material && !this.isNewFile(material)) {
				this.deletedMaterials.push(material)
				console.log(
					'🗑️ Материал добавлен в deletedMaterials:',
					this.deletedMaterials,
				)
			}
			this.materials.splice(index, 1)
		},
		validateSizesBeforeSave() {
			this.videoFileError = ''
			this.videoCoverError = ''
			this.materialsError = ''
			this.docFileError = ''

			if (this.videoFile && this.videoFile.size > VIDEO_MAX_SIZE_BYTES) {
				this.videoFileError = `Файл "${this.videoFile.name}" слишком большой. Максимальный размер: ${VIDEO_MAX_SIZE_MB} МБ.`
				return false
			}

			if (
				this.isNewFile(this.videoCoverFile) &&
				this.videoCoverFile.size > VIDEO_COVER_MAX_SIZE_BYTES
			) {
				this.videoCoverError = `Файл "${this.videoCoverFile.name}" слишком большой. Максимальный размер: ${VIDEO_COVER_MAX_SIZE_MB} МБ.`
				return false
			}

			if (this.docFile) {
				const size = this.docFile.size || this.docFile.file_size
				if (size && size > MATERIAL_MAX_SIZE_BYTES) {
					const name = this.docFile.name || this.docFile.file_name || 'Документ'
					this.docFileError = `Файл "${name}" слишком большой. Максимальный размер: ${MATERIAL_MAX_SIZE_MB} МБ.`
					return false
				}
			}

			for (const file of this.materials) {
				const size = file.size || file.file_size
				if (size && size > MATERIAL_MAX_SIZE_BYTES) {
					const name = file.name || file.file_name || 'Файл'
					this.materialsError = `Файл "${name}" слишком большой. Максимальный размер каждого файла: ${MATERIAL_MAX_SIZE_MB} МБ.`
					return false
				}
			}

			return true
		},
		handleSaveLesson() {
			if (!this.validateSizesBeforeSave()) return
			if (!this.validateLessonLogic()) return
			if (this.isSaving) return
			this.isSaving = true

			const isArticle = this.lessonType === 'article'

			const lessonData = {
				...(this.editingLessonId && { id: this.editingLessonId }),
				module: this.currentModuleId,
				title: this.lessonTitle,
				type: this.lessonType,
				description: this.normalizeRichText(this.lessonDescription),
				content: isArticle ? this.articleContent || '' : '',
				articleContent: isArticle ? this.articleContent || '' : '',
				publishType: this.isScheduledPublish ? 'scheduled' : 'immediate',
				publishDate: this.isScheduledPublish ? this.publishDate : '',
				publishTime: this.isScheduledPublish ? this.publishTime : '',
				videoFile: this.lessonType === 'video' ? this.videoFile : null,
				videoUrl: this.lessonType === 'video' ? this.videoLink || '' : '',
				videoCoverFile:
					this.lessonType === 'video' && this.isNewFile(this.videoCoverFile)
						? this.videoCoverFile
						: null,
				videoCoverUrl:
					this.lessonType === 'video' &&
					!this.isNewFile(this.videoCoverFile)
						? this.videoCoverPreviewUrl || ''
						: '',
				cover_image_url:
					this.lessonType === 'video' &&
					!this.isNewFile(this.videoCoverFile)
						? this.videoCoverPreviewUrl || null
						: null,
				docFile: isArticle ? this.docFile : null,
				deletedDocFile: isArticle ? this.deletedDocFile : false,
				materials: this.materials,
				deletedMaterials: this.deletedMaterials,
				duration: 30,

				// Настройки статьи
				enableToc: isArticle ? this.enableToc : false,
				tocMode: isArticle ? this.tocMode : 'auto',

				// Ограничения скачивания
				disableMainMaterialsDownload: this.disableMainMaterialsDownload,
				disableAdditionalMaterialsDownload:
					this.disableAdditionalMaterialsDownload,
			}
			this.lastSaveTime = Date.now()
			// Очищаем сохраненные данные формы после успешного сохранения
			this.clearFormData()
			this.isSaved = true
			this.captureOriginalData()
			if (typeof navigator !== 'undefined' && !navigator.onLine) {
				this.enqueueSubmit('save-lesson', lessonData)
				alert('Нет интернета. Сохранение урока поставлено в очередь.')
				setTimeout(() => {
					this.isSaving = false
				}, 300)
				return
			}
			this.$emit('save-lesson', lessonData)
			setTimeout(() => {
				this.isSaving = false
			}, 300)
		},
		handleSaveDraft() {
			if (this.isSaving) return
			this.isSaving = true
			const isArticle = this.lessonType === 'article'

			const draftData = {
				...(this.editingLessonId && { id: this.editingLessonId }),
				module: this.currentModuleId,
				title: this.lessonTitle,
				type: this.lessonType,
				description: this.normalizeRichText(this.lessonDescription),
				content: isArticle ? this.articleContent || '' : '',
				articleContent: isArticle ? this.articleContent || '' : '',
				publishType: this.isScheduledPublish ? 'scheduled' : 'immediate',
				publishDate: this.isScheduledPublish ? this.publishDate : '',
				publishTime: this.isScheduledPublish ? this.publishTime : '',
				videoFile: this.lessonType === 'video' ? this.videoFile : null,
				videoUrl: this.lessonType === 'video' ? this.videoLink || '' : '',
				videoCoverFile:
					this.lessonType === 'video' && this.isNewFile(this.videoCoverFile)
						? this.videoCoverFile
						: null,
				videoCoverUrl:
					this.lessonType === 'video' &&
					!this.isNewFile(this.videoCoverFile)
						? this.videoCoverPreviewUrl || ''
						: '',
				cover_image_url:
					this.lessonType === 'video' &&
					!this.isNewFile(this.videoCoverFile)
						? this.videoCoverPreviewUrl || null
						: null,
				docFile: isArticle ? this.docFile : null,
				deletedDocFile: isArticle ? this.deletedDocFile : false,
				materials: this.materials,
				deletedMaterials: this.deletedMaterials,
				duration: 30,

				// Настройки статьи
				enableToc: isArticle ? this.enableToc : false,
				tocMode: isArticle ? this.tocMode : 'auto',

				// Ограничения скачивания
				disableMainMaterialsDownload: this.disableMainMaterialsDownload,
				disableAdditionalMaterialsDownload:
					this.disableAdditionalMaterialsDownload,

				isDraft: true,
				is_draft: true,
			}
			// Очищаем сохраненные данные формы после сохранения черновика
			this.clearFormData()
			this.isSaved = true
			this.captureOriginalData()

			if (typeof navigator !== 'undefined' && !navigator.onLine) {
				this.enqueueSubmit('save-draft', draftData)
				alert('Нет интернета. Сохранение черновика поставлено в очередь.')
				setTimeout(() => {
					this.isSaving = false
				}, 300)
				return
			}
			this.$emit('save-draft', draftData)
			setTimeout(() => {
				this.isSaving = false
			}, 300)
		},

		handleClose() {
			//  Проверяем реальные изменения относительно исходных данных
			const hasRealChanges = this.hasUnsavedChanges

			if (hasRealChanges) {
				const confirmed = window.confirm(
					'У вас есть несохраненные изменения. Вы уверены, что хотите закрыть форму?',
				)
				if (!confirmed) return
			}

			//  Сохраняем ID ДО очистки формы (важно!)
			const scrollToId = this.editingLessonId

			// Очищаем сохраненные данные формы
			this.clearFormData()

			//  Передаём объект с ID для скролла
			this.$emit('cancel', { scrollToId, type: 'lesson' })
		},

		handleSaveArticle() {
			if (!this.hasArticleText(this.articleContent)) {
				alert('Добавьте текст статьи перед сохранением.')
				return
			}
			this.lessonType = 'article'
			// Создаём превью без HTML-тегов (первые 300 символов)
			this.lessonDescription = this.getPlainText(this.articleContent).slice(
				0,
				300,
			)
			this.handleSaveLesson()
		},

		sanitizePreviewHtml(html = '') {
			return DOMPurify.sanitize(html || '', {
				ALLOWED_TAGS: [
					'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'del', 'ins',
					'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
					'blockquote', 'code', 'pre', 'hr', 'figure', 'figcaption', 'img',
					'table', 'caption', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
					'colgroup', 'col', 'div', 'span', 'label', 'input', 'small',
					'mark', 'sub', 'sup', 'oembed',
				],
				ALLOWED_ATTR: [
					'href', 'target', 'rel', 'src', 'srcset', 'sizes', 'alt', 'title',
					'class', 'id', 'style', 'width', 'height', 'colspan', 'rowspan',
					'type', 'checked', 'disabled', 'value', 'name', 'url', 'start',
					'reversed', 'dir', 'lang', 'aria-label',
				],
				ALLOW_DATA_ATTR: true,
				FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed'],
				FORBID_ATTR: ['on*'],
			})
		},
		getPlainText(html = '') {
			const div = document.createElement('div')
			div.innerHTML = html
			return (div.textContent || '').replace(/\u00A0/g, ' ').trim()
		},
		hasMeaningfulText(html = '') {
			const raw = String(html || '')
			if (!raw.trim()) return false
			if (/<(img|table|ul|ol|blockquote|pre|hr|input|oembed|figure|video|iframe)\b/i.test(raw)) {
				return true
			}
			return this.getPlainText(raw).length > 0
		},
		normalizeRichText(html = '') {
			const raw = String(html || '')
			return this.hasMeaningfulText(raw) ? raw : ''
		},
		hasArticleText(html = '') {
			return this.hasMeaningfulText(html)
		},
		toggleArticleEditor() {
			if (!this.canEditArticleContent) {
				return
			}
			this.showArticleEditor = !this.showArticleEditor
		},
		openLessonPreviewInNewTab() {
			if (!this.canOpenLessonPreview) {
				alert('Сначала сохраните модуль и урок, затем откройте предпросмотр.')
				return
			}

			openInternalRouteInContext(this.$router, {
				name: 'TeacherScreen8Preview',
				query: {
					moduleId: String(this.currentModuleId),
					previewType: 'lesson',
					lessonId: String(this.editingLessonId),
					section: 'lessons',
					testMode: '1',
				},
			}, {
				title: 'Предпросмотр урока',
				leftBoundary: 48,
			})
		},

		handlePreview() {
			if (
				!this.lessonTitle &&
				!this.hasMeaningfulText(this.lessonDescription) &&
				!this.hasArticleText(this.articleContent)
			) {
				this.materialsError =
					'Заполните хотя бы заголовок или описание, чтобы открыть предпросмотр.'
				return
			}

			this.showPreviewModal = true
		},

		handleClosePreview() {
			this.showPreviewModal = false
		},

		removeVideo() {
			if (!window.confirm('Удалить видео?')) {
				return
			}
			this.clearVideo()
		},

		// ============================================
		// МЕТОДЫ ДЛЯ РАБОТЫ С LOCALSTORAGE
		// ============================================

		getFormDataKey() {
			return `lessonCreationForm_${this.currentModuleId}_formData`
		},

		saveFormData() {
			// Не сохраняем данные, если форма в режиме редактирования
			if (this.editingLessonId) return

			const formData = {
				lessonTitle: this.lessonTitle,
				lessonType: this.lessonType,
				lessonDescription: this.lessonDescription,
				articleContent: this.articleContent,
				videoLink: this.videoLink,
				isScheduledPublish: this.isScheduledPublish,
				publishDate: this.publishDate,
				publishTime: this.publishTime,
				enableToc: this.enableToc,
				tocMode: this.tocMode,
				disableMainMaterialsDownload: this.disableMainMaterialsDownload,
				disableAdditionalMaterialsDownload:
					this.disableAdditionalMaterialsDownload,
				timestamp: Date.now(),
			}

			try {
				localStorage.setItem(this.getFormDataKey(), JSON.stringify(formData))
			} catch (error) {
				console.error('❌ Ошибка сохранения данных формы:', error)
			}
		},

		restoreFormData() {
			// Не восстанавливаем данные, если форма в режиме редактирования
			if (this.editingLessonId) return

			try {
				const savedData = localStorage.getItem(this.getFormDataKey())
				if (savedData) {
					const formData = JSON.parse(savedData)

					// Проверяем, что данные не старше 24 часов
					const isExpired =
						Date.now() - formData.timestamp > 24 * 60 * 60 * 1000

					if (!isExpired) {
						console.log('🔄 Восстановление данных формы:', formData)

						this.lessonTitle = formData.lessonTitle || ''
						this.lessonType = formData.lessonType || 'video'
						this.lessonDescription = formData.lessonDescription || ''
						this.articleContent = formData.articleContent || ''
						this.showArticleEditor = this.hasArticleText(this.articleContent)
						this.videoLink = formData.videoLink || ''
						this.isScheduledPublish = formData.isScheduledPublish || false
						this.publishDate = formData.publishDate || ''
						this.publishTime = formData.publishTime || ''
						this.enableToc = !!formData.enableToc
						this.tocMode = formData.tocMode || 'auto'
						this.disableMainMaterialsDownload =
							!!formData.disableMainMaterialsDownload
						this.disableAdditionalMaterialsDownload =
							!!formData.disableAdditionalMaterialsDownload
					} else {
						// Удаляем устаревшие данные
						this.clearFormData()
						console.log('🗑️ Удалены устаревшие данные формы')
					}
				}
			} catch (error) {
				console.error('❌ Ошибка восстановления данных формы:', error)
				this.clearFormData()
			}
		},

		clearFormData() {
			try {
				localStorage.removeItem(this.getFormDataKey())
				console.log('🗑️ Данные формы очищены из localStorage')
			} catch (error) {
				console.error('❌ Ошибка очистки данных формы:', error)
			}
		},

		//  НОВЫЕ МЕТОДЫ для отслеживания изменений

		/**
		 * Захватывает текущее состояние формы как исходное
		 */
		captureOriginalData() {
			this.originalData = {
				lessonTitle: this.lessonTitle,
				lessonType: this.lessonType,
				lessonDescription: this.lessonDescription,
				articleContent: this.articleContent,
				videoLink: this.videoLink,
				isScheduledPublish: this.isScheduledPublish,
				publishDate: this.publishDate,
				publishTime: this.publishTime,
				// Для файлов сохраняем только имена/пути для сравнения
				videoFileName: this.videoFile
					? this.videoFile.name || this.videoFile.file_name || ''
					: '',
				videoCoverFileName: this.videoCoverFile
					? this.videoCoverFile.name || this.videoCoverFile.file_name || ''
					: '',
				docFileName: this.docFile
					? this.docFile.name || this.docFile.file_name || ''
					: '',
				materialFileNames: this.materials.map(m => m.name || m.file_name || ''),
				deletedDocFile: this.deletedDocFile,
				// Настройки статьи
				enableToc: this.enableToc,
				tocMode: this.tocMode,
				// Ограничения скачивания
				disableMainMaterialsDownload: this.disableMainMaterialsDownload,
				disableAdditionalMaterialsDownload:
					this.disableAdditionalMaterialsDownload,
			}
			console.log('📸 Снимок исходных данных:', this.originalData)
		},
	},
}
</script>

<style scoped>
.form-offline-banner {
	margin-bottom: 12px;
	padding: 10px 12px;
	border: 1px solid #f59e0b;
	border-radius: 8px;
	background: #fffbeb;
	color: #92400e;
	font-size: 13px;
	font-weight: 500;
}

.lesson-actions__button--disabled {
	opacity: 0.5;
	cursor: not-allowed;
	background-color: #9ca3af !important;
	border: none !important;
	box-shadow: none !important;
}

.lesson-actions__button--primary:not(.lesson-actions__button--disabled):hover {
	opacity: 0.9;
	transform: translateY(-1px);
	transition: all 0.2s;
}

.lesson-content__toggle-article-button {
	padding: 10px 14px;
	border: 1px solid #d1d5db;
	border-radius: 8px;
	background: #ffffff;
	color: #111827;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s ease;
}

.lesson-content__toggle-article-button:hover {
	border-color: #0095ec;
	color: #0095ec;
}

/* CSS переменные для единообразия */
.lesson-content__toggle-article-button--disabled,
.lesson-content__toggle-article-button:disabled {
	cursor: not-allowed;
	opacity: 0.6;
	border-color: #d1d5db;
	color: #6b7280;
	background: #f9fafb;
}

.lesson-content__toggle-article-button--disabled:hover,
.lesson-content__toggle-article-button:disabled:hover {
	border-color: #d1d5db;
	color: #6b7280;
}

.lesson-content__toggle-article-hint {
	margin-top: 8px;
	color: #6b7280;
	font-size: 13px;
	line-height: 1.4;
}

:root {
	--primary-color: #0095ec;
	--primary-light: #e6f3ff;
	--border-color: #d1d5db;
	--border-light: #e5e7eb;
	--text-primary: #111827;
	--text-secondary: #4b5563;
	--text-muted: #6b7280;
	--text-placeholder: #9ca3af;
	--background-light: #f3f4f6;
	--border-radius: 6px;
	--transition: all 0.15s ease;
}

.practice-settings__close-icon1 {
	width: 18px;
	height: 18px;
	filter: brightness(0) invert(1);
}

.lesson-creation-form {
	font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI',
		sans-serif;
	/* background: #f5f7fb; */
	min-height: 100vh;
	/* padding: 32px 0; */
	color: #1f2933;
	box-sizing: border-box;
}

/* основной контейнер формы */

.lesson-creation-form__container {
	position: relative;
	max-width: 980px;
	margin: 0 auto;
	background: #ffffff;
	border-radius: 12px;
	border: 1px solid #e5e7eb;
	/* box-shadow: 0 12px 30px rgba(148, 163, 184, 0.35); */
	padding: 24px 32px 0;
	box-sizing: border-box;
}

/* фон из разметки не нужен — прячем */

.lesson-creation-form__background {
	display: none;
}

/* левая часть и правая теперь идут просто одна под другой */

.lesson-settings {
	padding: 0;
	border-bottom: 1px solid #e5e7eb;
	margin-bottom: 24px;
}

.lesson-content {
	padding: 0;
}

/* шапка настроек (заголовок + крестик) */

.lesson-settings__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 16px;
	margin-left: -32px;
	/* margin-right: -32px; */
	margin-top: -24px;
	width: 978px;

	background: #9ca3af;
	padding: 16px 20px;
	border-radius: 12px 12px 0 0;
	position: relative;
}

.lesson-settings__header-background {
	display: none;
}

.lesson-settings__header-title {
	font-size: 18px;
	font-weight: 500;
	color: #ffffff;
	margin: 0;
}

.lesson-settings__close-button {
	width: 24px;
	height: 24px;
	border: none;
	background: transparent;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: opacity 0.15s ease;
	color: #ffffff;
}

.lesson-settings__close-button:hover {
	opacity: 0.8;
}

.lesson-settings__close-icon {
	font-size: 18px;
	font-weight: 300;
	color: #ffffff;
	line-height: 1;
}

/* общие поля сверху (название и т.п.) */

.lesson-settings__field {
	margin-bottom: 16px;
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.lesson-settings__label {
	color: #4b5563;
	font-family: 'Poppins', Helvetica;
	font-size: 17px;
	font-weight: 500;
	letter-spacing: 0;
	line-height: normal;
	margin: 0 0 15px 0;
}

.lesson-settings__input {
	border-radius: 6px;
	border: 1px solid #d1d5db;
	padding: 8px 10px;
	font-size: 13px;
	outline: none;
	transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.lesson-settings__input::placeholder {
	color: #9ca3af;
}

.lesson-settings__input:focus {
	border-color: #0095ec;
	box-shadow: 0 0 0 1px rgba(0, 149, 236, 0.25);
}

.lesson-settings__input--error {
	border-color: #e11d48;
	box-shadow: 0 0 0 1px rgba(225, 29, 72, 0.15);
}

.lesson-settings__autosave {
	margin-top: -6px;
	margin-bottom: 8px;
}

.lesson-settings__autosave-text {
	font-size: 11px;
	color: #9ca3af;
}

/* Блок "Тип урока" */

.lesson-settings__type-section {
	margin-top: 16px;
	margin-bottom: 18px;
}

.lesson-settings__section-title {
	color: #4b5563;
	font-family: 'Poppins', Helvetica;
	font-size: 17px;
	font-weight: 500;
	letter-spacing: 0;
	line-height: normal;
	margin: 0 0 15px 0;
}

.lesson-settings__type-buttons {
	display: flex;
	gap: 12px;
}

.lesson-type-btn {
	flex: 1;
	border-radius: 6px;
	border: 0.5px solid #d1d5db;
	background: #ffffff;
	padding: 10px 12px;
	cursor: pointer;
	transition: all 0.15s ease;
	text-align: left;
	height: 150px;
	max-width: 550px;
}

.lesson-type-btn__heading {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 20px;
}

.lesson-type-btn__icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 45px;
	height: 32px;
	border-radius: 4px;
}

.lesson-type-btn__title {
	color: #4b5563;
	font-family: 'Poppins', Helvetica;
	font-size: 17px;
	font-weight: 600;
	letter-spacing: 0;
	line-height: normal;
}

.lesson-type-btn__description {
	font-size: 13px;
	color: #6b7280;
	margin-left: 50px;
}

.lesson-type-btn--active {
	border-color: #0095ec;
	background: #e6f3ff;
	box-shadow: 0 0 0 1px rgba(0, 149, 236, 0.3);
}

/* заголовок "Содержание урока" */

.lesson-content__title {
	color: #4b5563;
	font-family: 'Poppins', Helvetica;
	font-size: 17px;
	font-weight: 500;
	letter-spacing: 0;
	line-height: normal;
	margin: 0 0 15px 0;
}

/* блоки внутри содержания: "Загрузите видео", "Описание" и т.п. */

.lesson-section {
	margin-bottom: 24px;
}

.lesson-section__title {
	color: #4b5563;
	font-family: 'Poppins', Helvetica;
	font-size: 15px;
	font-weight: 500;
	letter-spacing: 0;
	line-height: normal;
	margin: 0 0 15px 0;
}

.lesson-section__card {
	border-radius: 6px;
	border: 1px solid #e5e7eb;
	background: #ffffff;
	padding: 12px 14px;
}

/* общие поля в контенте */

.lesson-content__field {
	display: flex;
	flex-direction: column;
	gap: 6px;
	margin-bottom: 8px;
}

.lesson-content__cover-upload {
	margin-top: 10px;
}

.lesson-content__cover-preview {
	margin: 8px 0;
	width: 180px;
	max-width: 100%;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	overflow: hidden;
	background: #f9fafb;
}

.lesson-content__cover-image {
	display: block;
	width: 100%;
	height: 102px;
	object-fit: cover;
}

.lesson-content__label {
	color: #4b5563;
	font-family: 'Poppins', Helvetica;
	font-size: 15px;
	font-weight: 500;
	letter-spacing: 0;
	line-height: normal;
	margin: 0 0 15px 0;
}

.lesson-required-mark {
	color: #e11d48;
	margin-left: 4px;
}

.lesson-content__input {
	border-radius: 6px;
	border: 1px solid #d1d5db;
	padding: 8px 10px;
	font-size: 13px;
	outline: none;
	transition: border-color 0.15s ease, box-shadow 0.15s ease;
	width: 100%;
}

.lesson-content__input::placeholder {
	color: #9ca3af;
}

.lesson-content__input:focus {
	border-color: #0095ec;
	box-shadow: 0 0 0 1px rgba(0, 149, 236, 0.25);
}

/* upload видео */

.lesson-content__file-input {
	display: none;
}

.lesson-file-upload__label {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	border-radius: 8px;
	background: #0095ec;
	color: #ffffff;
	font-size: 13px;
	font-weight: 500;
	padding: 7px 14px;
	cursor: pointer;
	border: none;
	transition: background 0.15s ease, box-shadow 0.15s ease;
}

.lesson-file-upload__label:hover {
	background: #0080cb;
	box-shadow: 0 6px 14px rgba(0, 149, 236, 0.35);
}

.lesson-file-upload__icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
}

/* стили для кнопки загрузки материалов */
.lesson-materials-upload {
	display: inline-block;
}

.lesson-materials-upload__label {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	border-radius: 8px;
	background: #0095ec;
	color: #ffffff;
	font-size: 13px;
	font-weight: 500;
	padding: 7px 14px;
	cursor: pointer;
	border: none;
	transition: background 0.15s ease, box-shadow 0.15s ease;
}

.lesson-materials-upload__label:hover {
	background: #0080cb;
	box-shadow: 0 6px 14px rgba(0, 149, 236, 0.35);
}

.lesson-materials-upload__icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
}

.lesson-materials-upload__hint {
	margin-left: 12px;
	display: inline-block;
}

.lesson-materials-upload__description {
	font-size: 12px;
	color: #6b7280;
}

.lesson-materials-upload__size-hint {
	font-size: 11px;
	color: #9ca3af;
}

/* маленькие подсказки и ошибки */

.lesson-content__hint {
	font-size: 11px;
	color: #9ca3af;
}

.lesson-content__hint--hidden {
	visibility: hidden;
}

.lesson-content__error {
	font-size: 11px;
	color: #e11d48;
}

/* строка ввода URL + кнопка "Проверить" */

.lesson-content__url-row {
	display: flex;
	align-items: stretch;
	gap: 8px;
}

.lesson-content__check-button {
	border-radius: 8px;
	border: none;
	background: #0095ec;
	color: #ffffff;
	font-size: 13px;
	font-weight: 500;
	padding: 0 16px;
	cursor: pointer;
	white-space: nowrap;
	transition: background 0.15s ease, box-shadow 0.15s ease;
}

.lesson-content__check-button:hover {
	background: #0080cb;
	box-shadow: 0 6px 14px rgba(0, 149, 236, 0.35);
}

/* превью видео под полями, как отдельный блок */

.lesson-content__preview {
	margin-bottom: 24px;
	border-radius: 6px;
	border: 1px solid #e5e7eb;
	overflow: hidden;
	background: #000;
}

.lesson-video {
	display: block;
	width: 100%;
	height: 320px;
	border: none;
}

/* голубой блок "Дополнительные материалы" */

.lesson-section--materials .lesson-section__card {
	background: #eaf5ff;
	border-color: #cfe3ff;
}

.lesson-section--materials .lesson-content__field {
	flex-direction: row;
	align-items: center;
	gap: 16px;
	margin-bottom: 0;
	flex-wrap: wrap;
}

.lesson-section--materials .lesson-content__hint {
	margin-left: 4px;
}

/* textarea "Описание урока" */

.lesson-content__textarea {
	width: 100%;
	min-height: 140px;
	border-radius: 6px;
	border: 1px solid #d1d5db;
	padding: 8px 10px;
	font-size: 13px;
	line-height: 1.5;
	resize: vertical;
	outline: none;
	transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.lesson-content__textarea::placeholder {
	color: #9ca3af;
}

.lesson-content__textarea:focus {
	border-color: #0095ec;
	box-shadow: 0 0 0 1px rgba(0, 149, 236, 0.25);
}

/* списки файлов под полями */

.lesson-materials-list {
	margin-top: 10px;
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.lesson-material-item,
.lesson-file-item {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 6px 8px;
	border-radius: 4px;
	background: #ffffff;
	border: 1px solid #e5e7eb;
}

.lesson-material-item__info,
.lesson-file-item__info {
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
}

.lesson-material-item__name,
.lesson-file-item__name {
	font-size: 12px;
	font-weight: 500;
	color: #111827;
	max-width: 320px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.lesson-material-item__size,
.lesson-file-item__size {
	font-size: 11px;
	color: #6b7280;
}

.lesson-material-item__badge {
	font-size: 10px;
	text-transform: uppercase;
	color: #059669;
	background: #ecfdf3;
	border-radius: 999px;
	padding: 1px 5px;
}

.lesson-material-item__remove,
.lesson-file-item__remove {
	width: 20px;
	height: 20px;
	border-radius: 999px;
	border: none;
	background: #f3f4f6;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 12px;
	color: #6b7280;
	cursor: pointer;
	transition: background 0.15s ease, color 0.15s ease;
}

.lesson-material-item__remove:hover,
.lesson-file-item__remove:hover {
	background: #fee2e2;
	color: #b91c1c;
}

/* футер */

.lesson-actions {
	margin-top: 28px;
	margin-left: -32px;
	margin-right: -32px;
	padding: 12px 32px;
	background: #f5f6f8;
	border-top: 1px solid #e5e7eb;
	display: flex;
	align-items: flex-start;
	gap: 10px;
	border-radius: 0 0 12px 12px;
}

.lesson-actions__primary-group {
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
}

.lesson-actions__error {
	position: absolute;
	top: calc(100% + 6px);
	left: 0;
	margin: 0;
	font-size: 12px;
	line-height: 1.4;
	color: #e11d48;
	max-width: 420px;
	z-index: 2;
}

.lesson-actions__button {
	border-radius: 8px;
	font-size: 13px;
	font-weight: 500;
	padding: 8px 16px;
	border: 1px solid transparent;
	cursor: pointer;
	white-space: nowrap;
	transition: background 0.15s ease, border-color 0.15s ease,
		box-shadow 0.15s ease;
}

/* "Предпросмотр" слева */

.lesson-actions__button--ghost {
	background: transparent;
	color: #4b5563;
	border-color: transparent;
	margin-right: auto; /* уходит влево, остальные кнопки прижимаются вправо */
}

.lesson-actions__preview-tooltip {
	display: inline-flex;
	margin-right: auto;
}

.lesson-actions__button--ghost:hover {
	background: #e5e7eb;
}

/* "Сохранить как черновик" — светлая кнопка */

.lesson-actions__button--secondary {
	background: #ffffff;
	color: #111827;
	border-color: #d1d5db;
}

.lesson-actions__button--secondary:hover {
	background: #f9fafb;
	box-shadow: 0 4px 10px rgba(148, 163, 184, 0.45);
}

/* "Создать урок" — синяя кнопка справа */

.lesson-actions__button--primary {
	background: #0095ec;
	color: #ffffff;
	border-color: #0095ec;
	box-shadow: 0 5px 14px rgba(37, 99, 235, 0.4);
}

.lesson-actions__button--primary:hover {
	background: #0080cb;
}

/* стили для блока загрузки документа статьи */
.lesson-section--doc-upload .lesson-section__card--upload {
	background: #eaf5ff;
	border-color: #cfe3ff;
	padding: 16px;
}

.lesson-doc-upload {
	display: flex;
	align-items: center;
	gap: 16px;
	flex-wrap: wrap;
}

.lesson-doc-upload__label {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	border-radius: 8px;
	background: #0095ec;
	color: #ffffff;
	font-size: 13px;
	font-weight: 500;
	padding: 7px 14px;
	cursor: pointer;
	border: none;
	transition: background 0.15s ease, box-shadow 0.15s ease;
}

.lesson-doc-upload__label:hover {
	background: #0080cb;
	box-shadow: 0 6px 14px rgba(0, 149, 236, 0.35);
}

.lesson-doc-upload__icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
}

.lesson-doc-upload__hint {
	flex: 1;
}

.lesson-doc-upload__description {
	font-size: 12px;
	color: #6b7280;
}

.lesson-section--description :deep(.topic-text-editor__toolbar .ck-toolbar) {
	border: 0;
	border-radius: 0;
	background: transparent;
	padding: 0 0 8px;
	border-bottom: 1px solid #e5e7eb;
}

.lesson-section--description :deep(.topic-text-editor__surface .ck.ck-editor__editable) {
	border: 0;
	border-radius: 0;
	padding: 12px 0 0;
}

.lesson-section--description :deep(.topic-text-editor__surface .ck.ck-editor__editable.ck-focused) {
	border: 0;
	box-shadow: none;
}

/* Keep editor height in normal document flow for this form */
.lesson-section--article-editor :deep(.rich-text-editor) {
	height: auto;
	min-height: 0;
}

.lesson-section--article-editor :deep(.editor-main-container) {
	overflow-anchor: none;
}

.lesson-section--article-editor :deep(.editor-page-container) {
	overflow-anchor: none;
}

/* стили для редактора статьи */
.article-editor {
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	overflow: hidden;
}

.article-editor__toolbar {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 12px;
	background: #f9fafb;
	border-bottom: 1px solid #e5e7eb;
}

.article-editor__font-select {
	border: 1px solid #d1d5db;
	border-radius: 4px;
	padding: 4px 8px;
	font-size: 12px;
	background: white;
	outline: none;
}

.article-editor__tools {
	display: flex;
	align-items: center;
	gap: 4px;
}

.article-editor__tool {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 28px;
	height: 28px;
	border: none;
	background: transparent;
	border-radius: 4px;
	cursor: pointer;
	color: #4b5563;
	transition: background 0.15s ease;
}

.article-editor__tool:hover {
	background: #e5e7eb;
}

.article-editor__title-field {
	border-bottom: 1px solid #e5e7eb;
}

.article-editor__title-input {
	width: 100%;
	border: none;
	padding: 12px 16px;
	font-size: 16px;
	font-weight: 500;
	outline: none;
	background: transparent;
}

.article-editor__title-input::placeholder {
	color: #9ca3af;
}

.article-editor__content-field {
	background: white;
}

.article-editor__content-textarea {
	width: 100%;
	border: none;
	padding: 16px;
	font-size: 14px;
	line-height: 1.6;
	resize: vertical;
	outline: none;
	background: transparent;
	min-height: 300px;
}

.article-editor__content-textarea::placeholder {
	color: #9ca3af;
}

.article-editor__actions {
	display: flex;
	justify-content: flex-end;
	margin-top: 16px;
}

.article-editor__save-btn {
	background: #0095ec;
	color: white;
	border: none;
	border-radius: 6px;
	padding: 8px 16px;
	font-size: 13px;
	font-weight: 500;
	cursor: pointer;
	transition: background 0.15s ease;
}

.article-editor__save-btn:hover {
	background: #0080cb;
}

/* адаптив */

@media (max-width: 768px) {
	.lesson-creation-form__container {
		margin: 0 12px;
		padding: 20px 16px 0;
	}

	.lesson-actions {
		margin-left: -16px;
		margin-right: -16px;
		padding: 10px 16px;
		flex-wrap: wrap;
	}

	.lesson-actions__primary-group {
		flex: 0 1 auto;
		width: auto;
	}

	.lesson-content__url-row {
		flex-direction: column;
	}
}

/* ============================================
   ПРЕДПРОСМОТР УРОКА - Production Ready
   ============================================ */

.modal-fade-enter-active,
.modal-fade-leave-active {
	transition: opacity 0.3s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
	opacity: 0;
}

.lesson-preview-overlay {
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.7);
	backdrop-filter: blur(4px);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 9999;
	padding: 20px;
}

.lesson-preview {
	background: #fff;
	border-radius: 16px;
	width: 100%;
	max-width: 900px;
	max-height: 90vh;
	display: flex;
	flex-direction: column;
	box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
	overflow: hidden;
}

.lesson-preview__header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	padding: 24px 28px;
	background: linear-gradient(135deg, #0095ec 0%, #0077cc 100%);
	color: #fff;
}

.lesson-preview__header-content {
	flex: 1;
}

.lesson-preview__badge {
	display: inline-block;
	padding: 4px 12px;
	background: rgba(255, 255, 255, 0.2);
	border-radius: 20px;
	font-size: 13px;
	font-weight: 500;
	margin-bottom: 8px;
}

.lesson-preview__title {
	font-size: 24px;
	font-weight: 700;
	margin: 0;
	line-height: 1.3;
}

.lesson-preview__close {
	background: rgba(255, 255, 255, 0.15);
	border: none;
	border-radius: 8px;
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	color: #fff;
	transition: background 0.2s;
	flex-shrink: 0;
	margin-left: 16px;
}

.lesson-preview__close:hover {
	background: rgba(255, 255, 255, 0.25);
}

.lesson-preview__body {
	flex: 1;
	overflow-y: auto;
	padding: 28px;
}

.lesson-preview__video-section {
	margin-bottom: 24px;
}

.lesson-preview__video-wrapper {
	position: relative;
	width: 100%;
	padding-bottom: 56.25%;
	background: #000;
	border-radius: 12px;
	overflow: hidden;
}

.lesson-preview__video-iframe,
.lesson-preview__video-player {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	border: none;
}

.lesson-preview__video-placeholder {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 60px 20px;
	background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
	border-radius: 12px;
	border: 2px dashed #d0d5dd;
}

.lesson-preview__video-placeholder-icon {
	font-size: 48px;
	margin-bottom: 12px;
	opacity: 0.5;
}

.lesson-preview__video-placeholder-text {
	color: #667085;
	font-size: 15px;
	margin: 0;
}

.lesson-preview__section-title {
	font-size: 16px;
	font-weight: 600;
	color: #1d2939;
	margin: 0 0 12px 0;
	display: flex;
	align-items: center;
	gap: 8px;
}

.lesson-preview__description {
	margin-bottom: 24px;
	padding: 20px;
	background: #f9fafb;
	border-radius: 12px;
	border-left: 4px solid #0095ec;
}

.lesson-preview__description-text {
	color: #475467;
	font-size: 15px;
	line-height: 1.7;
	margin: 0;
	white-space: pre-wrap;
}

.lesson-preview__document {
	margin-bottom: 24px;
}

.lesson-preview__document-item {
	display: flex;
	align-items: center;
	gap: 16px;
	padding: 16px 20px;
	background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
	border-radius: 12px;
	border: 1px solid #fed7aa;
}

.lesson-preview__document-icon {
	font-size: 32px;
	flex-shrink: 0;
}

.lesson-preview__document-info {
	flex: 1;
	min-width: 0;
}

.lesson-preview__document-name {
	display: block;
	font-size: 15px;
	font-weight: 600;
	color: #9a3412;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.lesson-preview__document-size {
	display: block;
	font-size: 13px;
	color: #c2410c;
	margin-top: 4px;
}

.lesson-preview__article {
	margin-bottom: 24px;
}

.lesson-preview__article-content {
	color: #344054;
	font-size: 15px;
	line-height: 1.8;
}

.lesson-preview__article-content h1,
.lesson-preview__article-content h2,
.lesson-preview__article-content h3 {
	color: #1d2939;
	margin-top: 24px;
	margin-bottom: 12px;
}

.lesson-preview__article-content p {
	margin-bottom: 16px;
}

.lesson-preview__article-content ul,
.lesson-preview__article-content ol {
	padding-left: 24px;
	margin-bottom: 16px;
}

.lesson-preview__article-content code {
	background: #f2f4f7;
	padding: 2px 6px;
	border-radius: 4px;
	font-family: 'Fira Code', monospace;
	font-size: 14px;
}

.lesson-preview__materials {
	margin-bottom: 24px;
}

.lesson-preview__materials-count {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 24px;
	height: 24px;
	padding: 0 8px;
	background: #0095ec;
	color: #fff;
	border-radius: 12px;
	font-size: 13px;
	font-weight: 600;
}

.lesson-preview__materials-list {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.lesson-preview__material-item {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px 16px;
	background: #f9fafb;
	border-radius: 10px;
	border: 1px solid #eaecf0;
	transition: all 0.2s;
}

.lesson-preview__material-item:hover {
	background: #f2f4f7;
	border-color: #d0d5dd;
}

.lesson-preview__material-icon {
	font-size: 24px;
	flex-shrink: 0;
}

.lesson-preview__material-info {
	flex: 1;
	min-width: 0;
}

.lesson-preview__material-name {
	display: block;
	font-size: 14px;
	font-weight: 500;
	color: #344054;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.lesson-preview__material-size {
	display: block;
	font-size: 12px;
	color: #667085;
	margin-top: 2px;
}

.lesson-preview__material-download {
	background: none;
	border: none;
	font-size: 18px;
	cursor: pointer;
	padding: 8px;
	border-radius: 6px;
	transition: background 0.2s;
}

.lesson-preview__material-download:hover {
	background: #e4e8ec;
}

.lesson-preview__meta {
	padding: 16px 20px;
	background: #f9fafb;
	border-radius: 10px;
	margin-top: 8px;
}

.lesson-preview__meta-item {
	display: flex;
	align-items: center;
	gap: 8px;
}

.lesson-preview__meta-label {
	font-size: 14px;
	color: #667085;
}

.lesson-preview__meta-value {
	font-size: 14px;
	font-weight: 500;
	color: #344054;
}

.lesson-preview__footer {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 16px 28px;
	background: #f9fafb;
	border-top: 1px solid #eaecf0;
}

.lesson-preview__footer-hint {
	font-size: 13px;
	color: #667085;
}

.lesson-preview__footer-btn {
	padding: 10px 24px;
	background: #0095ec;
	color: #fff;
	border: none;
	border-radius: 8px;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s;
}

.lesson-preview__footer-btn:hover {
	background: #0077cc;
	transform: translateY(-1px);
}

@media (max-width: 768px) {
	.lesson-preview-overlay {
		padding: 0;
	}

	.lesson-preview {
		max-height: 100vh;
		border-radius: 0;
	}

	.lesson-preview__header {
		padding: 16px 20px;
	}

	.lesson-preview__title {
		font-size: 18px;
	}

	.lesson-preview__body {
		padding: 20px;
	}

	.lesson-preview__footer {
		flex-direction: column;
		gap: 12px;
		text-align: center;
	}
}

/* ========== НОВЫЕ НАСТРОЙКИ СТАТЬИ И СКАЧИВАНИЯ ========== */

.lesson-settings-row {
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 14px;
}

.lesson-settings-row--indent {
	margin-left: 28px;
}

.lesson-settings__select {
	border-radius: 6px;
	border: 1px solid #d1d5db;
	padding: 8px 12px;
	font-size: 14px;
	font-family: 'Poppins', Helvetica;
	outline: none;
	background-color: #ffffff;
	min-width: 200px;
	cursor: pointer;
	transition: border-color 0.15s ease;
}

.lesson-settings__select:focus {
	border-color: #0095ec;
}

.lesson-settings__input--small {
	width: 100px;
}

/* Кастомный чекбокс */
.lesson-checkbox {
	display: flex;
	align-items: center;
	gap: 10px;
	cursor: pointer;
	user-select: none;
}

.lesson-checkbox__input {
	display: none;
}

.lesson-checkbox__mark {
	width: 20px;
	height: 20px;
	border: 2px solid #d1d5db;
	border-radius: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.15s ease;
	flex-shrink: 0;
}

.lesson-checkbox__input:checked + .lesson-checkbox__mark {
	background-color: #0095ec;
	border-color: #0095ec;
}

.lesson-checkbox__input:checked + .lesson-checkbox__mark::after {
	content: '✓';
	color: #ffffff;
	font-size: 14px;
	font-weight: bold;
}

.lesson-checkbox__label {
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	color: #374151;
}
</style>
